import React, { useState, useEffect } from 'react';
import { Analytics } from "@vercel/analytics/react";
import { ChevronRight, ChevronLeft, AlertCircle, BookOpen, Menu, Twitter, Facebook, Linkedin, Mail, Link2, User, Pill, Heart } from 'lucide-react';

const ROTATING_TEXT = [
  'most optimal formula',
  'most effective dose',
  'the best stack'
];

const SupplementOptimizer = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    age: '',
    gender: '',
    supplement: '',
    goal: '',
    allergies: [],
    medications: '',
    dietaryRestrictions: []
  });
  const [results, setResults] = useState([]);
  const [researchSummary, setResearchSummary] = useState('');
  const [personalizedDosage, setPersonalizedDosage] = useState('');
  const [recommendedForm, setRecommendedForm] = useState('');
  const [interactionWarning, setInteractionWarning] = useState('');
  const [, setIsSearching] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [error, setError] = useState('');
  const [expandedDosage, setExpandedDosage] = useState(true);
  const [expandedResearch, setExpandedResearch] = useState(true);
  const [expandedStack, setExpandedStack] = useState(true);
  const [stackRecommendations, setStackRecommendations] = useState([]);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [currentPage, setCurrentPage] = useState('home'); // home, privacy, terms, about, contact, blog, blog-magnesium, blog-omega3, blog-vitamin-d, blog-quality-guide, blog-tag, supplement
  const [selectedTag, setSelectedTag] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [supplementSlug, setSupplementSlug] = useState(null);
  const [supplementPageData, setSupplementPageData] = useState(null);
  const [supplementPageLoading, setSupplementPageLoading] = useState(false);
  const [wizardStep, setWizardStep] = useState(1); // 1=Personal, 2=Supplement, 3=Health, 4=Preferences
  const [headlineIndex, setHeadlineIndex] = useState(0);

  // Rotate text
  useEffect(() => {
    const interval = setInterval(() => {
      setHeadlineIndex((prev) => (prev + 1) % ROTATING_TEXT.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // URL-based routing
  useEffect(() => {
    const handleUrl = () => {
      const path = window.location.pathname;

      // Handle /supplements/:slug URLs
      if (path.startsWith('/supplements/')) {
        const slug = path.replace('/supplements/', '');
        if (slug) {
          setSupplementSlug(slug);
          setCurrentPage('supplement');
          loadSupplementPage(slug);
        }
      } else if (path === '/privacy') {
        setCurrentPage('privacy');
      } else if (path === '/terms') {
        setCurrentPage('terms');
      } else if (path === '/about') {
        setCurrentPage('about');
      } else if (path === '/contact') {
        setCurrentPage('contact');
      } else if (path === '/blog') {
        setCurrentPage('blog');
      } else if (path === '/' || path === '') {
        setCurrentPage('home');
      }
    };

    // Handle initial URL
    handleUrl();

    // Handle browser back/forward
    window.addEventListener('popstate', handleUrl);
    return () => window.removeEventListener('popstate', handleUrl);
  }, []);

  // Load supplement page data
  const loadSupplementPage = async (slug) => {
    setSupplementPageLoading(true);
    try {
      const response = await fetch(`/api/supplements/${slug}`);
      const data = await response.json();
      if (data.found) {
        setSupplementPageData(data.data);
      } else {
        setSupplementPageData(null);
      }
    } catch (error) {
      console.error('Error loading supplement page:', error);
      setSupplementPageData(null);
    }
    setSupplementPageLoading(false);
  };

  // Navigation helper that scrolls to top and updates URL
  const navigateTo = (page, slug = null) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);

    // Update URL
    if (page === 'supplement' && slug) {
      setSupplementSlug(slug);
      window.history.pushState({}, '', `/supplements/${slug}`);
      loadSupplementPage(slug);
    } else if (page === 'home') {
      window.history.pushState({}, '', '/');
    } else if (['privacy', 'terms', 'about', 'contact', 'blog'].includes(page)) {
      window.history.pushState({}, '', `/${page}`);
    }
  };

  // Share buttons component for blog posts
  const ShareButtons = ({ title }) => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const encodedUrl = encodeURIComponent(url);
    const encodedTitle = encodeURIComponent(title);

    const copyLink = () => {
      navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    };

    return (
      <div className="flex items-center justify-center gap-3">
        <span className="text-xs text-gray-400 mr-1">Share:</span>
        <a
          href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 rounded-full bg-white/60 hover:bg-white/80 backdrop-blur-sm border border-gray-200/50 text-gray-500 hover:text-gray-700 transition-colors shadow-sm"
          title="Share on Twitter"
        >
          <Twitter size={16} />
        </a>
        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 rounded-full bg-white/60 hover:bg-white/80 backdrop-blur-sm border border-gray-200/50 text-gray-500 hover:text-gray-700 transition-colors shadow-sm"
          title="Share on Facebook"
        >
          <Facebook size={16} />
        </a>
        <a
          href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 rounded-full bg-white/60 hover:bg-white/80 backdrop-blur-sm border border-gray-200/50 text-gray-500 hover:text-gray-700 transition-colors shadow-sm"
          title="Share on LinkedIn"
        >
          <Linkedin size={16} />
        </a>
        <a
          href={`mailto:?subject=${encodedTitle}&body=Check out this article: ${encodedUrl}`}
          className="p-2 rounded-full bg-white/60 hover:bg-white/80 backdrop-blur-sm border border-gray-200/50 text-gray-500 hover:text-gray-700 transition-colors shadow-sm"
          title="Share via Email"
        >
          <Mail size={16} />
        </a>
        <button
          onClick={copyLink}
          className="p-2 rounded-full bg-white/60 hover:bg-white/80 backdrop-blur-sm border border-gray-200/50 text-gray-500 hover:text-gray-700 transition-colors shadow-sm"
          title="Copy link"
        >
          <Link2 size={16} />
        </button>
      </div>
    );
  };

  // Configuration
  const USE_REAL_API = true;
  const API_BASE_URL = '';
  // TODO: Add your Amazon affiliate tag when approved (e.g. "vitaminbird-20")
  const AMAZON_TAG = "";

  // Clean up recommended form for search query (remove dosages, verbose text)
  const getSearchTerm = (recommendedForm) => {
    if (!recommendedForm) return '';

    return recommendedForm
      // Remove dosage numbers and units
      .replace(/\d+[\s-]*(?:mg|mcg|iu|cfu|billion|million|g|ml|oz)/gi, '')
      // Remove parenthetical content
      .replace(/\([^)]*\)/g, '')
      // Remove common filler phrases
      .replace(/with at least|at least|minimum|up to|per day|daily|per serving/gi, '')
      // Remove special characters except spaces and hyphens
      .replace(/[^\w\s-]/g, '')
      // Collapse multiple spaces
      .replace(/\s+/g, ' ')
      // Trim
      .trim();
  };

  // Get popular supplements with market average prices
  const getMockIHerbProducts = (supplement) => {
    const supplementLower = supplement.toLowerCase();
    
    if (supplementLower.includes('omega') || supplementLower.includes('fish oil')) {
      return [
        {
          id: 'ih1',
          productCode: "54306",
          name: "California Gold Nutrition Omega-3 Premium Fish Oil",
          brand: "California Gold Nutrition",
          form: "Triglyceride Form",
          dosage: "1000mg (180mg EPA, 120mg DHA)",
          optimality: 98,
          price: 14.00,
          pricePerServing: 0.23,
          cleanlinessScore: 97,
          certifications: ["Third-Party Tested", "Non-GMO"],
          countryOfOrigin: "USA",
          dirtyIngredients: [],
          image: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400",
          summary: "High-quality triglyceride form fish oil.",
          availableAt: ["Amazon"],
          affiliateLink: `https://www.amazon.com/s?k=Thorne+Omega-3+fish+oil${AMAZON_TAG ? '&tag=' + AMAZON_TAG : ''}`
        },
        {
          id: 'ih2',
          productCode: "71871",
          name: "Nordic Naturals Ultimate Omega",
          brand: "Nordic Naturals",
          form: "Triglyceride Form",
          dosage: "1280mg (650mg EPA, 450mg DHA)",
          optimality: 96,
          price: 32.36,
          pricePerServing: 0.54,
          cleanlinessScore: 98,
          certifications: ["Friend of the Sea", "Non-GMO"],
          countryOfOrigin: "Norway",
          dirtyIngredients: [],
          image: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400",
          summary: "Premium Norwegian fish oil.",
          availableAt: ["Amazon"],
          affiliateLink: `https://www.amazon.com/s?k=Nordic+Naturals+Ultimate+Omega${AMAZON_TAG ? '&tag=' + AMAZON_TAG : ''}`
        },
        {
          id: 'ih3',
          productCode: "60600",
          name: "Now Foods Ultra Omega-3",
          brand: "Now Foods",
          form: "Fish Oil Concentrate",
          dosage: "750mg EPA, 250mg DHA",
          optimality: 90,
          price: 19.49,
          pricePerServing: 0.32,
          cleanlinessScore: 92,
          certifications: ["GMP Certified"],
          countryOfOrigin: "USA",
          dirtyIngredients: [],
          image: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400",
          summary: "High-potency fish oil.",
          availableAt: ["Amazon"],
          affiliateLink: `https://www.amazon.com/s?k=Now+Foods+Ultra+Omega-3${AMAZON_TAG ? '&tag=' + AMAZON_TAG : ''}`
        },
        {
          id: 'ih4',
          productCode: "45373",
          name: "Jarrow Formulas EPA-DHA Balance",
          brand: "Jarrow Formulas",
          form: "Natural Triglyceride",
          dosage: "600mg Omega-3",
          optimality: 88,
          price: 18.95,
          pricePerServing: 0.32,
          cleanlinessScore: 93,
          certifications: ["Friend of the Sea"],
          countryOfOrigin: "USA",
          dirtyIngredients: [],
          image: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400",
          summary: "Balanced EPA/DHA ratio.",
          availableAt: ["Amazon"],
          affiliateLink: `https://www.amazon.com/s?k=Jarrow+Formulas+EPA-DHA+Balance${AMAZON_TAG ? '&tag=' + AMAZON_TAG : ''}`
        },
        {
          id: 'ih5',
          productCode: "89432",
          name: "Carlson Elite Omega-3",
          brand: "Carlson",
          form: "Norwegian Triglyceride",
          dosage: "1600mg (800mg EPA, 500mg DHA)",
          optimality: 94,
          price: 34.50,
          pricePerServing: 0.58,
          cleanlinessScore: 96,
          certifications: ["IFOS Certified"],
          countryOfOrigin: "Norway",
          dirtyIngredients: [],
          image: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400",
          summary: "Premium Norwegian fish oil.",
          availableAt: ["Amazon"],
          affiliateLink: `https://www.amazon.com/s?k=Carlson+Elite+Omega-3${AMAZON_TAG ? '&tag=' + AMAZON_TAG : ''}`
        }
      ];
    } else if (supplementLower.includes('magnesium')) {
      return [
        {
          id: 'ih1',
          productCode: "68959",
          name: "Doctor's Best High Absorption Magnesium",
          brand: "Doctor's Best",
          form: "Magnesium Glycinate",
          dosage: "200mg Elemental",
          optimality: 98,
          price: 14.56,
          pricePerServing: 0.24,
          cleanlinessScore: 95,
          certifications: ["Non-GMO", "Vegan"],
          countryOfOrigin: "USA",
          dirtyIngredients: [],
          image: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400",
          summary: "Patented TRAACS chelated magnesium.",
          availableAt: ["Amazon"],
          affiliateLink: `https://www.amazon.com/s?k=Doctor%27s+Best+High+Absorption+Magnesium${AMAZON_TAG ? '&tag=' + AMAZON_TAG : ''}`
        },
        {
          id: 'ih2',
          productCode: "74558",
          name: "Natural Factors Magnesium Citrate",
          brand: "Natural Factors",
          form: "Magnesium Citrate",
          dosage: "150mg Elemental",
          optimality: 92,
          price: 9.22,
          pricePerServing: 0.15,
          cleanlinessScore: 93,
          certifications: ["Non-GMO"],
          countryOfOrigin: "Canada",
          dirtyIngredients: [],
          image: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400",
          summary: "Highly absorbable citrate form.",
          availableAt: ["Amazon"],
          affiliateLink: `https://www.amazon.com/s?k=Natural+Factors+Magnesium+Citrate${AMAZON_TAG ? '&tag=' + AMAZON_TAG : ''}`
        },
        {
          id: 'ih3',
          productCode: "71872",
          name: "Life Extension Neuro-Mag",
          brand: "Life Extension",
          form: "Magnesium L-Threonate",
          dosage: "144mg Elemental",
          optimality: 96,
          price: 32.25,
          pricePerServing: 1.08,
          cleanlinessScore: 97,
          certifications: ["Non-GMO"],
          countryOfOrigin: "USA",
          dirtyIngredients: [],
          image: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400",
          summary: "Brain-specific magnesium.",
          availableAt: ["Amazon"],
          affiliateLink: `https://www.amazon.com/s?k=Life+Extension+Neuro-Mag+Magnesium+L-Threonate${AMAZON_TAG ? '&tag=' + AMAZON_TAG : ''}`
        },
        {
          id: 'ih4',
          productCode: "56039",
          name: "Now Foods Magnesium Glycinate",
          brand: "Now Foods",
          form: "Magnesium Bisglycinate",
          dosage: "400mg Elemental",
          optimality: 90,
          price: 18.14,
          pricePerServing: 0.30,
          cleanlinessScore: 89,
          certifications: ["GMP Certified"],
          countryOfOrigin: "USA",
          dirtyIngredients: ["Stearic Acid"],
          image: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400",
          summary: "High-potency chelated form.",
          availableAt: ["Amazon"],
          affiliateLink: `https://www.amazon.com/s?k=Now+Foods+Magnesium+Glycinate${AMAZON_TAG ? '&tag=' + AMAZON_TAG : ''}`
        }
      ];
    } else {
      return [
        {
          id: 'ih1',
          productCode: "GEN1",
          name: supplement + " Complex",
          brand: "California Gold Nutrition",
          form: "Optimal form",
          dosage: "Clinical dosage",
          optimality: 95,
          price: 19.99,
          pricePerServing: 0.33,
          cleanlinessScore: 94,
          certifications: ["Third-Party Tested"],
          countryOfOrigin: "USA",
          dirtyIngredients: [],
          image: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400",
          summary: "High-quality " + supplement + ".",
          availableAt: ["Amazon"],
          affiliateLink: `https://www.amazon.com/s?k=${encodeURIComponent(supplement)}+supplement${AMAZON_TAG ? '&tag=' + AMAZON_TAG : ''}`
        }
      ];
    }
  };

  const searchProducts = async (supplement) => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    const iHerbProducts = getMockIHerbProducts(supplement);
    return iHerbProducts;
  };

  const interpretSupplementAndGoal = async (supplement, goal) => {
    if (USE_REAL_API) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/interpret`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ supplement, goal })
        });
        const data = await response.json();
        return data;
      } catch (err) {
        console.error('API error:', err);
        return { supplement: supplement || "general supplement", goal: goal || "general well-being" };
      }
    } else {
      // Mock response when USE_REAL_API is false
      await new Promise(resolve => setTimeout(resolve, 500));
      return {
        supplement: supplement || "general supplement",
        goal: goal || "general well-being"
      };
    }
  };

  const checkInteractions = async (supplement, medications, age, gender) => {
    if (!medications || medications.trim() === '') return '';

    if (USE_REAL_API) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/check-interactions`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ supplement, medications, age, gender })
        });
        const data = await response.json();
        return data.warning || '';
      } catch (err) {
        console.error('API error:', err);
        return '';
      }
    } else {
      // Mock response when USE_REAL_API is false
      await new Promise(resolve => setTimeout(resolve, 800));
      return `**NO MAJOR INTERACTIONS DETECTED** for ${supplement} with ${medications}.\n\nHowever, always consult your healthcare provider before starting new supplements, especially when taking medications. Some supplements can affect medication absorption or effectiveness.`;
    }
  };

  const calculatePersonalizedDosage = async (supplement, goal, age, gender) => {
    if (USE_REAL_API) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/calculate-dosage`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ supplement, goal, age, gender })
        });
        const data = await response.json();
        return data.dosage || '';
      } catch (err) {
        console.error('API error:', err);
        return '';
      }
    } else {
      // Mock response when USE_REAL_API is false
      await new Promise(resolve => setTimeout(resolve, 1000));
      return `**Optimal Form**: For ${supplement}, look for highly bioavailable forms such as chelated minerals or pharmaceutical-grade formulations from reputable third-party tested brands.\n\n**Recommended Dosage**: For ${age}-year-old ${gender} targeting ${goal}, typical dosages range from moderate to clinical levels. Start with the lower end and adjust based on individual response.\n\n**Timing & Absorption**: Best taken with food to enhance absorption and minimize digestive discomfort. Consult with a healthcare provider for personalized dosing recommendations.`;
    }
  };

  const searchResearch = async (supplement, goal) => {
    if (USE_REAL_API) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/search-research`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ supplement, goal })
        });
        const data = await response.json();

        if (data.error) {
          throw new Error(data.error);
        }

        return data.research || '';
      } catch (err) {
        console.error('API error:', err);
        throw new Error("Failed to fetch research");
      }
    } else {
      // Mock response when USE_REAL_API is false
      await new Promise(resolve => setTimeout(resolve, 1200));
      return `### Effectiveness
Multiple clinical studies suggest that ${supplement} may be beneficial for ${goal}. Research indicates moderate to strong evidence supporting its use in various populations.

### Optimal Form
Look for bioavailable forms such as chelated minerals, liposomal delivery, or pharmaceutical-grade preparations. Third-party testing (USP, NSF, or ConsumerLab) ensures quality and purity.

### Dosage
Clinical trials typically use doses ranging from low to moderate levels. Individual needs may vary based on age, gender, and health status. Always start at the lower end of the dosage range.

### Synergistic Compounds
${supplement} works well when combined with complementary nutrients that enhance absorption and effectiveness. Consider cofactors and supporting vitamins for optimal results.

*Note: For real-time PubMed research and specific study citations, enable the API integration. This is a demonstration using mock data.*`;
    }
  };

  const handleNext = async () => {
    if (step === 1) {
      if (!formData.supplement && !formData.goal) {
        setError("Please provide either a supplement name or your health goal");
        return;
      }

      setStep(2);
      setIsSearching(true);
      setLoadingStep(0);
      setError('');

      try {
        if (USE_REAL_API) {
          // Use single API call for efficiency
          setLoadingStep(1);
          const response = await fetch(`${API_BASE_URL}/api/search`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              supplement: formData.supplement,
              goal: formData.goal,
              age: formData.age,
              gender: formData.gender,
              medications: formData.medications,
              allergies: formData.allergies,
              dietaryRestrictions: formData.dietaryRestrictions
            })
          });

          const data = await response.json();

          if (data.error) {
            throw new Error(data.error);
          }

          setLoadingStep(2);
          const finalSupplement = data.supplement || formData.supplement;
          const finalGoal = formData.goal;
          setFormData({ ...formData, supplement: finalSupplement, goal: finalGoal });

          setLoadingStep(3);
          setInteractionWarning(data.interactionWarning || '');

          setLoadingStep(4);
          // Build formatted dosage from new structured fields
          let dosageText = '';
          if (data.optimalForm) {
            dosageText += `**Optimal Formulation:** ${data.optimalForm}\n\n`;
            setRecommendedForm(data.optimalForm);
          } else {
            setRecommendedForm(finalSupplement);
          }
          if (data.recommendedDose) {
            dosageText += `**Dose:** ${data.recommendedDose}\n\n`;
          }
          if (data.dosageNotes) {
            dosageText += data.dosageNotes;
          }
          // Fallback to old format if new fields not present
          setPersonalizedDosage(dosageText || data.personalizedDosage || '');

          setLoadingStep(5);
          setResearchSummary(data.researchSummary || '');
          setStackRecommendations(data.stackRecommendations || []);

          // Get products (still using mock for now)
          const products = await searchProducts(finalSupplement);
          setResults(products);

          setIsSearching(false);
          setStep(3);
        } else {
          // Mock mode - use individual function calls
          setLoadingStep(1);
          const interpreted = await interpretSupplementAndGoal(formData.supplement, formData.goal);
          const finalSupplement = interpreted.supplement;
          const finalGoal = interpreted.goal;

          setFormData({ ...formData, supplement: finalSupplement, goal: finalGoal });

          setLoadingStep(2);
          const interactions = await checkInteractions(finalSupplement, formData.medications, formData.age, formData.gender);
          setInteractionWarning(interactions);

          setLoadingStep(3);
          const dosage = await calculatePersonalizedDosage(finalSupplement, finalGoal, formData.age, formData.gender);
          setPersonalizedDosage(dosage);

          setLoadingStep(4);
          const research = await searchResearch(finalSupplement, finalGoal);
          setResearchSummary(research);

          setLoadingStep(5);
          const products = await searchProducts(finalSupplement);
          setResults(products);

          setIsSearching(false);
          setStep(3);
        }
      } catch (err) {
        setError(err.message);
        setIsSearching(false);
        setStep(1);
      }
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailAddress.trim()) {
      alert("Please enter a valid email address");
      return;
    }

    try {
      // Send actual email with results
      const response = await fetch(`${API_BASE_URL}/api/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailAddress,
          supplement: formData.supplement,
          goal: formData.goal,
          age: formData.age,
          gender: formData.gender,
          dosage: personalizedDosage,
          research: researchSummary,
          interactions: interactionWarning,
          products: sortedResults.slice(0, 5) // Send top 5 products
        })
      });

      const data = await response.json();

      if (data.success) {
        setEmailSent(true);

        // Reset after 5 seconds
        setTimeout(() => {
          setEmailSent(false);
          setEmailAddress('');
        }, 5000);
      } else {
        alert('Failed to send email. Please try again.');
      }
    } catch (error) {
      console.error('Email error:', error);
      alert('Failed to send email. Please check your connection and try again.');
    }
  };

  const sortedResults = [...results].sort((a, b) => b.optimality - a.optimality);

  const renderCompactMarkdown = (text, isExpanded) => {
    if (!text) return null;

    let cleanText = text
      .replace(/\*\*\*(.+?)\*\*\*/g, '<strong>$1</strong>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/###\s+(.+)/g, '<strong class="subheading">$1</strong>')
      .replace(/##\s+(.+)/g, '<strong class="subheading">$1</strong>')
      .replace(/#\s+(.+)/g, '<strong class="subheading">$1</strong>')
      .replace(/^\s*[-*]\s+/gm, '• ')  // Convert dashes/asterisks to bullet points
      .replace(/•\s*•/g, '•')  // Remove double bullets
      .replace(/\s+,/g, ',')
      .replace(/\s+\./g, '.')
      .replace(/\n{3,}/g, '\n');

    // Split by bullet points or newlines to create separate items
    let lines = cleanText.split(/(?=•)|(?:\n)/).filter(line => line.trim());
    const previewLines = isExpanded ? lines : lines.slice(0, 3);
    
    return (
      <>
        {previewLines.map((line, idx) => {
          const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
          const parts = [];
          let lastIndex = 0;
          let match;

          // Check if line is a bullet point and strip the bullet
          const isBullet = line.trim().startsWith('•');
          const processedLine = isBullet ? line.replace(/^•\s*/, '') : line;

          // Check if line has strong tags (subheadings)
          if (processedLine.includes('<strong class="subheading">')) {
            const subheadingRegex = /<strong class="subheading">(.+?)<\/strong>/g;
            let subMatch;
            let subLastIndex = 0;

            while ((subMatch = subheadingRegex.exec(processedLine)) !== null) {
              if (subMatch.index > subLastIndex) {
                parts.push(processedLine.substring(subLastIndex, subMatch.index));
              }
              
              // Determine emoji based on content
              let emoji = '📌';
              const content = subMatch[1].toLowerCase();
              if (content.includes('effectiveness')) emoji = '✅';
              else if (content.includes('form')) emoji = '💊';
              else if (content.includes('dosage') || content.includes('dose')) emoji = '📊';
              else if (content.includes('synergistic') || content.includes('compound')) emoji = '🔗';
              
              parts.push(
                <div key={idx + "-subheading-" + subMatch.index} className="font-bold text-purple-700 mt-2 mb-1 flex items-center gap-1">
                  <span>{emoji}</span>
                  <span>{subMatch[1]}</span>
                </div>
              );
              subLastIndex = subMatch.index + subMatch[0].length;
            }

            if (subLastIndex < processedLine.length) {
              const remainingText = processedLine.substring(subLastIndex);
              // Process links in remaining text
              const linkMatches = [...remainingText.matchAll(linkRegex)];
              let linkLastIndex = 0;
              linkMatches.forEach((m, mIdx) => {
                if (m.index > linkLastIndex) {
                  parts.push(remainingText.substring(linkLastIndex, m.index));
                }
                parts.push(
                  <a key={idx + "-link-" + mIdx} href={m[2]} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                    {m[1]}
                  </a>
                );
                linkLastIndex = m.index + m[0].length;
              });
              if (linkLastIndex < remainingText.length) {
                parts.push(remainingText.substring(linkLastIndex));
              }
            }
          } else if (processedLine.includes('<strong>')) {
            // Handle regular bold
            const boldRegex = /<strong>(.+?)<\/strong>/g;
            let boldMatch;
            let boldLastIndex = 0;

            while ((boldMatch = boldRegex.exec(processedLine)) !== null) {
              if (boldMatch.index > boldLastIndex) {
                parts.push(processedLine.substring(boldLastIndex, boldMatch.index));
              }
              parts.push(
                <strong key={idx + "-bold-" + boldMatch.index} className="font-semibold text-gray-900">
                  {boldMatch[1]}
                </strong>
              );
              boldLastIndex = boldMatch.index + boldMatch[0].length;
            }

            if (boldLastIndex < processedLine.length) {
              const remainingText = processedLine.substring(boldLastIndex);
              const linkMatches = [...remainingText.matchAll(linkRegex)];
              let linkLastIndex = 0;
              linkMatches.forEach((m, mIdx) => {
                if (m.index > linkLastIndex) {
                  parts.push(remainingText.substring(linkLastIndex, m.index));
                }
                parts.push(
                  <a key={idx + "-link-" + mIdx} href={m[2]} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                    {m[1]}
                  </a>
                );
                linkLastIndex = m.index + m[0].length;
              });
              if (linkLastIndex < remainingText.length) {
                parts.push(remainingText.substring(linkLastIndex));
              }
            }
          } else {
            // Regular text with potential links
            while ((match = linkRegex.exec(processedLine)) !== null) {
              if (match.index > lastIndex) {
                parts.push(processedLine.substring(lastIndex, match.index));
              }
              parts.push(
                <a key={idx + "-" + match.index} href={match[2]} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                  {match[1]}
                </a>
              );
              lastIndex = match.index + match[0].length;
            }

            if (lastIndex < processedLine.length) {
              parts.push(processedLine.substring(lastIndex));
            }
          }

          const displayLine = parts.length > 0 ? parts : processedLine;

          if (isBullet) {
            return (
              <div key={idx} className="mb-4 flex items-start gap-3">
                <span className="text-blue-500 text-2xl font-bold flex-shrink-0 leading-none">•</span>
                <span className="flex-1">{displayLine}</span>
              </div>
            );
          }
          return <div key={idx} className="mb-3">{displayLine}</div>;
        })}
        {!isExpanded && lines.length > 3 && <span className="text-gray-400 text-sm">...</span>}
      </>
    );
  };

  const bgColor = 'bg-gradient-to-br from-slate-50 via-white to-blue-50/30';
  const cardBg = 'bg-white/70 backdrop-blur-xl';
  const textColor = 'text-gray-900';
  const mutedColor = 'text-gray-600';
  const borderColor = 'border-gray-200/50';

  // Render different pages
  if (currentPage === 'privacy') {
    return (
      <div className={`min-h-screen ${bgColor} ${textColor}`}>
        <div className={cardBg + " border-b " + borderColor}>
          <div className="max-w-6xl mx-auto px-6 py-3">
            <button onClick={() => setCurrentPage('home')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <span className="text-2xl text-gray-900 tracking-tight"><span className="font-semibold">vitamin</span><span className="font-light">bird</span></span>
            </button>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-6 py-12">
          <h1 className="text-4xl font-bold mb-8">Privacy Policy</h1>
          <div className="space-y-6 text-sm leading-relaxed">
            <p className="text-xs text-gray-500">Last updated: {new Date().toLocaleDateString()}</p>

            <section>
              <h2 className="text-2xl font-bold mb-3">1. Information We Collect</h2>
              <p>Vitamin Bird ("we," "our," or "us") collects minimal information to provide our supplement research and comparison service. We collect:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Form data you submit (age, gender, supplement interests, health goals, allergies, medications)</li>
                <li>IP address for location detection (to show relevant pricing)</li>
                <li>Browser information and device type for service optimization</li>
                <li>Email address if you choose to receive results via email</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">2. How We Use Your Information</h2>
              <p>We use the collected information to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Provide personalized supplement recommendations based on your inputs</li>
                <li>Research relevant studies from PubMed and scientific databases</li>
                <li>Check for potential drug interactions with your medications</li>
                <li>Display region-appropriate pricing from iHerb</li>
                <li>Send results to your email address (if requested)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">3. Data Storage and Security</h2>
              <p>Your form submissions are processed in real-time and are not permanently stored on our servers. Health information entered (medications, allergies) is only used during your session to generate recommendations and is not retained after you close your browser.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">4. Third-Party Services</h2>
              <p>We use the following third-party services:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Mistral AI API:</strong> Processes your queries to research supplements and analyze scientific studies</li>
                <li><strong>Affiliate Partners:</strong> Affiliate links to supplement products (see Affiliate Disclosure)</li>
                <li><strong>IP Geolocation Service:</strong> Detects your country for regional pricing</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">5. Cookies and Tracking</h2>
              <p>We do not use cookies or third-party analytics at this time. All user preferences are stored locally in your browser.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">6. Your Rights</h2>
              <p>You have the right to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Request information about data we process</li>
                <li>Request deletion of your email address from our records</li>
                <li>Opt-out of email communications at any time</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">7. Children's Privacy</h2>
              <p>Vitamin Bird is not intended for users under 18 years of age. We do not knowingly collect information from children.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">8. Changes to This Policy</h2>
              <p>We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last updated" date.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">9. Contact Us</h2>
              <p>For privacy-related questions, please contact us through the Contact page.</p>
            </section>
          </div>
          <button onClick={() => setCurrentPage('home')} className="mt-8 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl font-medium hover:from-blue-600 hover:to-indigo-600 shadow-md shadow-blue-200/50 transition-all">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (currentPage === 'terms') {
    return (
      <div className={`min-h-screen ${bgColor} ${textColor}`}>
        <div className={cardBg + " border-b " + borderColor}>
          <div className="max-w-6xl mx-auto px-6 py-3">
            <button onClick={() => setCurrentPage('home')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <span className="text-2xl text-gray-900 tracking-tight"><span className="font-semibold">vitamin</span><span className="font-light">bird</span></span>
            </button>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-6 py-12">
          <h1 className="text-4xl font-bold mb-8">Terms of Service</h1>
          <div className="space-y-6 text-sm leading-relaxed">
            <p className="text-xs text-gray-500">Last updated: {new Date().toLocaleDateString()}</p>

            <section>
              <h2 className="text-2xl font-bold mb-3">1. Acceptance of Terms</h2>
              <p>By accessing and using Vitamin Bird, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our service.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">2. Medical Disclaimer</h2>
              <p><strong>IMPORTANT:</strong> Vitamin Bird is an informational tool only. We are not medical professionals, and nothing on this site constitutes medical advice.</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>All information is for educational purposes only</li>
                <li>AI-generated recommendations may contain errors or omissions</li>
                <li>Always consult your doctor before starting any supplement</li>
                <li>We are not liable for health outcomes resulting from use of our service</li>
                <li>Drug interaction warnings are informational only - verify with your pharmacist</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">3. Affiliate Relationships</h2>
              <p>Vitamin Bird participates in affiliate programs with partner retailers. When you click our product links and make a purchase, we may earn a commission at no extra cost to you. This helps support our service.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">4. Accuracy of Information</h2>
              <p>While we strive for accuracy, we make no guarantees about:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Current pricing (prices change frequently)</li>
                <li>Product availability</li>
                <li>Completeness of research summaries</li>
                <li>Accuracy of AI-generated recommendations</li>
              </ul>
              <p className="mt-2">Always verify information independently before making health decisions.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">5. User Conduct</h2>
              <p>You agree not to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Use our service for any illegal purposes</li>
                <li>Attempt to scrape, copy, or redistribute our content without permission</li>
                <li>Overload our servers with excessive requests</li>
                <li>Provide false or misleading information</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">6. Intellectual Property</h2>
              <p>The Vitamin Bird name, logo, design, and original content are our property. Product information and research summaries are provided for your personal, non-commercial use.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">7. Limitation of Liability</h2>
              <p>To the fullest extent permitted by law, Vitamin Bird and its operators are not liable for any damages arising from your use of our service, including but not limited to health complications, financial losses, or data issues.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">8. Changes to Service</h2>
              <p>We reserve the right to modify, suspend, or discontinue any part of our service at any time without notice.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">9. Governing Law</h2>
              <p>These terms are governed by applicable laws. Any disputes shall be resolved in accordance with local jurisdiction requirements.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">10. Contact</h2>
              <p>Questions about these terms? Contact us through our Contact page.</p>
            </section>
          </div>
          <button onClick={() => setCurrentPage('home')} className="mt-8 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl font-medium hover:from-blue-600 hover:to-indigo-600 shadow-md shadow-blue-200/50 transition-all">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (currentPage === 'about') {
    return (
      <div className={`min-h-screen ${bgColor} ${textColor}`}>
        <div className={cardBg + " border-b " + borderColor}>
          <div className="max-w-6xl mx-auto px-6 py-3">
            <button onClick={() => setCurrentPage('home')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <span className="text-2xl text-gray-900 tracking-tight"><span className="font-semibold">vitamin</span><span className="font-light">bird</span></span>
            </button>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-6 py-12">
          <h1 className="text-4xl font-bold mb-8">About Vitamin Bird</h1>
          <div className="space-y-6 text-sm leading-relaxed">

            <section>
              <h2 className="text-2xl font-bold mb-3">Our Mission</h2>
              <p>Vitamin Bird helps you find the most optimal supplements at the best prices online, backed by the latest scientific research from PubMed and other trusted medical databases.</p>
              <p className="mt-2">We believe supplement shopping should be informed by science, not marketing. Our AI-powered tool cuts through the noise to give you personalized recommendations based on peer-reviewed research.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">How It Works</h2>
              <ol className="list-decimal pl-6 mt-2 space-y-2">
                <li><strong>Tell us your goals:</strong> Share your age, gender, supplement interests, and health objectives</li>
                <li><strong>AI Research:</strong> Our system searches PubMed and scientific databases for relevant studies</li>
                <li><strong>Personalized Analysis:</strong> We analyze effectiveness, optimal forms, dosage recommendations, and potential interactions</li>
                <li><strong>Best Deals:</strong> Compare prices on iHerb to find the best value</li>
                <li><strong>Smart Filtering:</strong> Sort by formulation quality, price, cleanliness score, or best deals</li>
              </ol>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">What Makes Us Different</h2>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Evidence-Based:</strong> All recommendations are grounded in peer-reviewed research</li>
                <li><strong>Transparent Pricing:</strong> See real-time prices and price-per-serving comparisons</li>
                <li><strong>Safety First:</strong> We check for drug interactions and highlight clean formulations</li>
                <li><strong>No Hidden Agenda:</strong> Our recommendations are based on research quality, not commission rates</li>
                <li><strong>Regional Support:</strong> Automatic detection for US and Canadian pricing</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">Technology</h2>
              <p>Vitamin Bird uses Claude AI (Anthropic) with web search capabilities to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Search and analyze current PubMed studies</li>
                <li>Identify optimal supplement forms and dosages</li>
                <li>Check for potential drug-supplement interactions</li>
                <li>Find synergistic compounds that enhance effectiveness</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">Important Notes</h2>
              <p className="bg-yellow-50 border border-yellow-200 rounded p-4">
                <strong>⚠️ Not Medical Advice:</strong> Vitamin Bird provides educational information only. Always consult your healthcare provider before starting any new supplement, especially if you have health conditions or take medications.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">Affiliate Disclosure</h2>
              <p>We participate in affiliate programs with partner retailers. When you purchase through our links, we earn a small commission at no extra cost to you. These commissions help us maintain and improve Vitamin Bird. Our recommendations are based on research quality and formulation, not commission rates.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">Contact Us</h2>
              <p>Have questions or feedback? Visit our Contact page to get in touch.</p>
            </section>
          </div>
          <button onClick={() => setCurrentPage('home')} className="mt-8 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl font-medium hover:from-blue-600 hover:to-indigo-600 shadow-md shadow-blue-200/50 transition-all">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (currentPage === 'contact') {
    return (
      <div className={`min-h-screen ${bgColor} ${textColor}`}>
        <div className={cardBg + " border-b " + borderColor}>
          <div className="max-w-6xl mx-auto px-6 py-3">
            <button onClick={() => setCurrentPage('home')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <span className="text-2xl text-gray-900 tracking-tight"><span className="font-semibold">vitamin</span><span className="font-light">bird</span></span>
            </button>
          </div>
        </div>
        <div className="max-w-2xl mx-auto px-6 py-12">
          <h1 className="text-4xl font-bold mb-8">Contact Us</h1>
          <div className="space-y-6">
            <p>We'd love to hear from you! Whether you have questions, feedback, or need support, please reach out.</p>

            <div className="bg-blue-50/70 backdrop-blur-lg border border-blue-200/60 rounded-2xl p-6 shadow-sm">
              <h2 className="text-xl font-bold mb-4">Get in Touch</h2>
              <div className="space-y-3 text-sm">
                <p><strong>Email:</strong> support@vitaminbird.com</p>
                <p><strong>Response Time:</strong> We typically respond within 24-48 hours</p>
              </div>
            </div>

            <div className="bg-white/60 backdrop-blur-lg border border-gray-200/60 rounded-2xl p-6 shadow-sm">
              <h2 className="text-xl font-bold mb-4">What to Include</h2>
              <p className="text-sm mb-2">To help us assist you better, please include:</p>
              <ul className="list-disc pl-6 text-sm space-y-1">
                <li>A clear description of your question or issue</li>
                <li>The supplement you were researching (if applicable)</li>
                <li>Any error messages you encountered</li>
                <li>Your browser and device type</li>
              </ul>
            </div>

            <div className="bg-amber-50/70 backdrop-blur-lg border border-amber-200/60 rounded-2xl p-6 shadow-sm">
              <h2 className="text-xl font-bold mb-4">⚠️ Medical Questions</h2>
              <p className="text-sm">We cannot provide medical advice. For health-related questions about supplements, medications, or conditions, please consult your doctor or pharmacist.</p>
            </div>

            <div className="bg-white/60 backdrop-blur-lg border border-gray-200/60 rounded-2xl p-6 shadow-sm">
              <h2 className="text-xl font-bold mb-4">Feedback & Suggestions</h2>
              <p className="text-sm">We're always looking to improve! If you have ideas for new features or ways to make Vitamin Bird better, we'd love to hear them.</p>
            </div>
          </div>
          <button onClick={() => setCurrentPage('home')} className="mt-8 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl font-medium hover:from-blue-600 hover:to-indigo-600 shadow-md shadow-blue-200/50 transition-all">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Supplement SEO page
  if (currentPage === 'supplement' && supplementSlug) {
    const supplementName = supplementPageData?.supplement || supplementSlug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    // Update document title for SEO
    if (typeof document !== 'undefined') {
      document.title = `${supplementName} - Dosage, Benefits & Best Forms | VitaminBird`;
    }

    return (
      <div className={`min-h-screen ${bgColor} ${textColor}`}>
        {/* Header */}
        <div className={cardBg + " border-b " + borderColor}>
          <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
            <button onClick={() => navigateTo('home')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <span className="text-2xl text-gray-900 tracking-tight"><span className="font-semibold">vitamin</span><span className="font-light">bird</span></span>
            </button>
            <a
              href={`https://www.amazon.com/s?k=${encodeURIComponent(supplementPageData?.optimalForm || supplementName)}${AMAZON_TAG ? '&tag=' + AMAZON_TAG : ''}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg text-sm font-bold hover:from-green-600 hover:to-green-700 transition-all shadow-sm"
            >
              Shop on Amazon →
            </a>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-12">
          {supplementPageLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-500">Loading supplement data...</p>
            </div>
          ) : supplementPageData ? (
            <>
              {/* Title */}
              <h1 className="text-4xl font-bold mb-2">{supplementPageData.supplement}</h1>
              <p className="text-lg text-gray-600 mb-8">Evidence-based dosage, benefits, and recommended forms</p>

              {/* Key Benefits */}
              {supplementPageData.keyBenefits && supplementPageData.keyBenefits.length > 0 && (
                <div className="bg-gradient-to-br from-blue-50/80 to-indigo-50/80 backdrop-blur-lg border border-blue-200/60 rounded-2xl p-6 shadow-sm mb-6">
                  <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <span>✨</span> Key Benefits
                  </h2>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {supplementPageData.keyBenefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-green-500 mt-0.5">✓</span>
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Optimal Dose & Form */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gradient-to-br from-green-50/80 to-emerald-50/80 backdrop-blur-lg border border-green-200/60 rounded-2xl p-6 shadow-sm">
                  <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
                    <Pill className="text-green-600" size={20} /> Optimal Form
                  </h2>
                  <p className="text-2xl font-bold text-green-700 mb-2">{supplementPageData.optimalForm}</p>
                  {supplementPageData.optimalForms && supplementPageData.optimalForms.length > 1 && (
                    <p className="text-sm text-gray-600">Other good forms: {supplementPageData.optimalForms.slice(1).join(', ')}</p>
                  )}
                </div>
                <div className="bg-gradient-to-br from-amber-50/80 to-yellow-50/80 backdrop-blur-lg border border-amber-200/60 rounded-2xl p-6 shadow-sm">
                  <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
                    <span>💊</span> Recommended Dose
                  </h2>
                  <p className="text-2xl font-bold text-amber-700 mb-2">{supplementPageData.recommendedDose}</p>
                  {supplementPageData.dosageNotes && (
                    <p className="text-sm text-gray-600">{supplementPageData.dosageNotes.split('\n')[0]}</p>
                  )}
                </div>
              </div>

              {/* Research Summary */}
              <div className="bg-white/70 backdrop-blur-lg border border-gray-200/60 rounded-2xl p-6 shadow-sm mb-6">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <BookOpen className="text-blue-600" size={20} /> Research Summary
                </h2>
                <div className="prose prose-sm max-w-none text-gray-700">
                  {supplementPageData.researchSummary?.split('\n').map((para, idx) => (
                    <p key={idx} className="mb-2" dangerouslySetInnerHTML={{
                      __html: para.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">$1</a>')
                    }} />
                  ))}
                </div>
              </div>

              {/* Stack Recommendations */}
              {supplementPageData.stackRecommendations && supplementPageData.stackRecommendations.length > 0 && (
                <div className="bg-gradient-to-br from-purple-50/80 to-blue-50/80 backdrop-blur-lg border border-purple-200/60 rounded-2xl p-6 shadow-sm mb-6">
                  <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <span>🧬</span> Recommended Stack
                  </h2>
                  <p className="text-sm text-gray-600 mb-4">These supplements pair well with {supplementPageData.supplement} for enhanced absorption and synergy.</p>
                  <div className="space-y-4">
                    {supplementPageData.stackRecommendations.map((item, idx) => {
                      const stackSlug = item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                      return (
                        <div key={idx} className="bg-white/70 rounded-xl p-4 border border-gray-200/50">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <h4 className="font-bold text-gray-900">
                                <button
                                  onClick={() => navigateTo('supplement', stackSlug)}
                                  className="text-purple-700 hover:text-purple-900 hover:underline"
                                >
                                  {item.name}
                                </button>
                              </h4>
                              <p className="text-sm text-purple-700 mt-1">{item.reason}</p>
                              <p className="text-xs text-gray-500 mt-2">
                                <span className="font-semibold">Form:</span> {item.optimalForm} • <span className="font-semibold">Dose:</span> {item.dose}
                              </p>
                              {item.citationLink ? (
                                <a
                                  href={item.citationLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 mt-2 text-xs text-blue-600 hover:text-blue-800 font-medium"
                                >
                                  <BookOpen size={12} />
                                  View PubMed Study →
                                </a>
                              ) : (
                                <span className="inline-flex items-center gap-1 mt-2 text-xs text-gray-400">
                                  <BookOpen size={12} />
                                  Search to load citation
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* CTA */}
              <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl p-6 text-white text-center">
                <h3 className="text-xl font-bold mb-2">Get Personalized Recommendations</h3>
                <p className="text-blue-100 mb-4">Use our free tool to get dosage recommendations based on your age, gender, and health goals.</p>
                <button
                  onClick={() => navigateTo('home')}
                  className="px-6 py-3 bg-white text-blue-600 rounded-xl font-bold hover:bg-blue-50 transition-colors"
                >
                  Try VitaminBird Free →
                </button>
              </div>

              {/* Disclaimer */}
              <p className="text-xs text-gray-400 text-center mt-6">
                This information is for educational purposes only and is not medical advice. Always consult with a healthcare provider before starting any supplement.
              </p>
            </>
          ) : (
            <div className="text-center py-12">
              <h1 className="text-2xl font-bold mb-4">Supplement Not Found</h1>
              <p className="text-gray-600 mb-6">We don't have data for "{supplementSlug.replace(/-/g, ' ')}" yet.</p>
              <button
                onClick={() => navigateTo('home')}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl font-bold hover:from-blue-600 hover:to-indigo-600 transition-all"
              >
                Search for it →
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Blog data with tags
  const allBlogPosts = [
    { id: 'blog-synthetic', title: 'In Defense of Synthetic Vitamins', excerpt: 'The "natural is better" myth falls apart when you do the math on how much food you\'d need to eat.', date: 'January 18, 2026', author: 'Rudy Scott', tags: ['synthetic vitamins', 'natural vs synthetic', 'bioavailability', 'food sources', 'dosing'] },
    { id: 'blog-magnesium', title: 'Why You Can\'t Get Enough Magnesium From Food Anymore', excerpt: 'Modern soil depletion, water treatment, and chronic stress have created a deficiency epidemic that diet alone can\'t fix.', date: 'January 15, 2026', author: 'Rudy Scott', tags: ['magnesium', 'deficiency', 'minerals', 'soil health', 'modern diet'] },
    { id: 'blog-omega3', title: 'Your Fish Oil Is Probably Rancid', excerpt: 'Studies show most omega-3 supplements are oxidized before you open the bottle. Here\'s what that means for your health.', date: 'January 12, 2026', author: 'Rudy Scott', tags: ['omega-3', 'fish oil', 'oxidation', 'quality control', 'inflammation'] },
    { id: 'blog-vitamin-d', title: 'The Vitamin D Mistake Almost Everyone Makes', excerpt: 'Taking high-dose vitamin D without its essential partner may be doing more harm than good.', date: 'January 10, 2026', author: 'Rudy Scott', tags: ['vitamin d', 'vitamin k2', 'calcium', 'bone health', 'cardiovascular'] },
    { id: 'blog-quality-guide', title: 'The Dark Side of B Vitamins', excerpt: 'Synthetic forms, toxic accumulation, and why "water-soluble means safe" is dangerously wrong.', date: 'January 8, 2026', author: 'Rudy Scott', tags: ['b vitamins', 'toxicity', 'synthetic vitamins', 'methylation', 'manufacturing'] }
  ];

  // Tag click handler
  const handleTagClick = (tag) => {
    setSelectedTag(tag);
    setCurrentPage('blog-tag');
    window.scrollTo(0, 0);
  };

  // Blog Tag Results Page
  if (currentPage === 'blog-tag' && selectedTag) {
    const filteredPosts = allBlogPosts.filter(post => post.tags.includes(selectedTag));

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
        <div className="bg-white/70 backdrop-blur-xl border-b border-gray-200/50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <button onClick={() => navigateTo('home')} className="flex items-center gap-2 hover:opacity-70 transition-opacity">
              <span className="text-xl text-gray-900 tracking-tight"><span className="font-semibold">vitamin</span><span className="font-light">bird</span></span>
            </button>
            <button onClick={() => navigateTo('blog')} className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1">
              <BookOpen size={16} />
              <span className="hidden sm:inline">All Articles</span>
            </button>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
          <nav className="text-sm text-gray-400 mb-6 flex items-center gap-2">
            <button onClick={() => navigateTo('home')} className="hover:text-gray-600">Home</button>
            <ChevronRight size={14} />
            <button onClick={() => navigateTo('blog')} className="hover:text-gray-600">Blog</button>
            <ChevronRight size={14} />
            <span className="text-gray-600">Tag</span>
          </nav>
          <h1 className="text-3xl sm:text-4xl font-serif mb-3 text-gray-900">Tagged: {selectedTag}</h1>
          <p className="text-gray-500 mb-16 text-lg">{filteredPosts.length} article{filteredPosts.length !== 1 ? 's' : ''} found</p>

          <div className="space-y-12">
            {filteredPosts.map(post => (
              <article key={post.id} className="cursor-pointer group" onClick={() => navigateTo(post.id)}>
                <p className="text-sm text-gray-400 mb-2">{post.date} · {post.author}</p>
                <h2 className="text-2xl font-serif text-gray-900 mb-2 group-hover:text-gray-600 transition-colors">{post.title}</h2>
                <p className="text-gray-600 leading-relaxed">{post.excerpt}</p>
              </article>
            ))}
          </div>

          <div className="mt-16 pt-8 border-t border-gray-200">
            <button onClick={() => navigateTo('blog')} className="text-gray-500 hover:text-gray-900 transition-colors">
              ← Back to all articles
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Blog Index Page
  if (currentPage === 'blog') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
        <div className="bg-white/70 backdrop-blur-xl border-b border-gray-200/50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <button onClick={() => navigateTo('home')} className="flex items-center gap-2 hover:opacity-70 transition-opacity">
              <span className="text-xl text-gray-900 tracking-tight"><span className="font-semibold">vitamin</span><span className="font-light">bird</span></span>
            </button>
            <button onClick={() => navigateTo('home')} className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1">
              <ChevronLeft size={16} />
              <span className="hidden sm:inline">Back to Search</span>
            </button>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
          <h1 className="text-3xl sm:text-4xl mb-3 text-gray-900 text-center tracking-tight">The <span className="font-semibold">vitamin</span><span className="font-light">bird</span> Blog</h1>
          <p className="text-gray-500 mb-10 sm:mb-16 text-lg text-center">Evidence-based research on vitamins, minerals, and supplementation.</p>

          <div className="space-y-8 sm:space-y-12">
            {allBlogPosts.map(post => (
              <article key={post.id} className="cursor-pointer group" onClick={() => navigateTo(post.id)}>
                <p className="text-sm text-gray-400 mb-2">{post.date} · {post.author}</p>
                <h2 className="text-xl sm:text-2xl font-serif text-gray-900 mb-2 group-hover:text-gray-600 transition-colors">{post.title}</h2>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{post.excerpt}</p>
              </article>
            ))}
          </div>

          <div className="mt-12 sm:mt-16 pt-8 border-t border-gray-200">
            <button onClick={() => navigateTo('home')} className="text-gray-500 hover:text-gray-900 transition-colors">
              ← Back to home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Blog Post: Magnesium Depletion
  if (currentPage === 'blog-magnesium') {
    const postTags = ['magnesium', 'deficiency', 'minerals', 'soil health', 'modern diet'];
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
        <div className="bg-white/70 backdrop-blur-xl border-b border-gray-200/50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <button onClick={() => navigateTo('home')} className="flex items-center gap-2 hover:opacity-70 transition-opacity">
              <span className="text-xl text-gray-900 tracking-tight"><span className="font-semibold">vitamin</span><span className="font-light">bird</span></span>
            </button>
            <button onClick={() => navigateTo('blog')} className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1">
              <BookOpen size={16} />
              <span className="hidden sm:inline">All Articles</span>
            </button>
          </div>
        </div>
        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
          <header className="mb-8 sm:mb-12">
            <nav className="text-sm text-gray-400 mb-6 flex items-center gap-2">
              <button onClick={() => navigateTo('home')} className="hover:text-gray-600">Home</button>
              <ChevronRight size={14} />
              <button onClick={() => navigateTo('blog')} className="hover:text-gray-600">Blog</button>
              <ChevronRight size={14} />
              <span className="text-gray-600">Article</span>
            </nav>
            <h1 className="text-4xl font-serif mb-4 text-gray-900 leading-tight">Why You Can't Get Enough Magnesium From Food Anymore</h1>
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-6">
              <span>Rudy Scott</span>
              <span>·</span>
              <span>January 15, 2026</span>
            </div>
            <ShareButtons title="Why You Can't Get Enough Magnesium From Food Anymore" />
          </header>

          <figure className="my-10">
            <img src="https://images.unsplash.com/photo-1500651230702-0e2d8a49d4ad?w=800&h=400&fit=crop" alt="Cracked, dry earth in an agricultural field" className="w-full h-64 object-cover rounded-lg" />
            <figcaption className="text-center text-gray-400 text-sm mt-3 italic">The ground beneath our feet no longer holds what it once did.</figcaption>
          </figure>

          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-6">
            <p className="text-xl text-gray-600 leading-relaxed">Your great-grandmother didn't need magnesium supplements. She ate vegetables grown in mineral-rich soil, drank water from wells that hadn't been stripped of minerals, and consumed far less of the things that deplete magnesium from the body. You live in a different world.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">The soil problem</h2>
            <p>Modern industrial agriculture has fundamentally changed the mineral content of our food. Crops are bred for yield, pest resistance, and appearance—not nutrient density. Fields are planted continuously without the fallow periods that once allowed soil to regenerate. <span className="bg-blue-100 px-1">Synthetic fertilizers replace nitrogen, phosphorus, and potassium, but not the dozens of trace minerals that plants once absorbed.</span></p>
            <p>A landmark study comparing USDA food composition data from 1950 to 1999 found significant declines in protein, calcium, phosphorus, iron, riboflavin, and vitamin C across 43 garden crops. Magnesium wasn't directly measured, but the pattern is clear: <em>the same apple your grandmother ate contained more nutrients than the one you buy today.</em></p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">Water treatment</h2>
            <p>Municipal water treatment removes magnesium along with other minerals. Reverse osmosis filtration, increasingly popular in homes, strips water even more thoroughly. Bottled water varies wildly—some brands contain decent magnesium, others are essentially empty.</p>
            <p>Our ancestors drank from streams, wells, and springs that picked up minerals from rock and soil. That incidental intake added up. <span className="bg-blue-100 px-1">Modern water is cleaner and safer, but it's also nutritionally barren.</span></p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">The depletion cascade</h2>
            <p>Even if you somehow obtained enough magnesium from diet, modern life would drain it away. Stress triggers magnesium excretion—and chronic stress, the background hum of contemporary existence, creates chronic depletion. Caffeine increases urinary magnesium loss. Alcohol does the same. Refined sugar requires magnesium for metabolism while providing none in return.</p>
            <p>Certain medications accelerate the problem. <span className="bg-blue-100 px-1">Proton pump inhibitors (like omeprazole), widely prescribed for acid reflux, reduce magnesium absorption so significantly that the FDA has issued warnings.</span> Diuretics flush magnesium out. Some antibiotics bind to it. The pharmaceutical solutions for modern ailments often worsen this particular modern ailment.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">Why testing misses it</h2>
            <p>Standard blood tests measure serum magnesium—the amount floating in your bloodstream. But <span className="bg-blue-100 px-1">only about 1% of your body's magnesium is in the blood.</span> The rest is in bones, muscles, and soft tissues. Your body works hard to maintain stable serum levels, pulling from reserves when necessary. By the time serum magnesium drops, you're severely depleted.</p>
            <p>This is why you can have normal blood work while experiencing every symptom of deficiency: muscle cramps, insomnia, anxiety, heart palpitations, fatigue. <em>The test says you're fine. Your body says otherwise.</em></p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">The symptoms are everywhere</h2>
            <p>Magnesium participates in over 300 enzymatic reactions. It's required for energy production, protein synthesis, muscle and nerve function, blood glucose control, and blood pressure regulation. Deficiency doesn't produce one obvious symptom—it produces a constellation of vague complaints that doctors often attribute to stress, aging, or anxiety.</p>
            <p>The irony: <span className="bg-blue-100 px-1">anxiety itself can be a magnesium deficiency symptom.</span> The supplement that might help is never prescribed because the patient is given an SSRI instead. Muscle cramps are dismissed as dehydration. Insomnia is treated with sleep aids that further deplete minerals. The cycle continues.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">What absorption actually requires</h2>
            <p>Not all magnesium supplements are equal, and the differences matter. <span className="bg-blue-100 px-1">Magnesium oxide, the cheapest and most common form, has roughly 4% absorption.</span> You're paying for a laxative effect, not systemic magnesium repletion. Citrate absorbs better. Glycinate better still, with the added benefit of glycine's calming effects. Threonate crosses the blood-brain barrier, making it interesting for cognitive symptoms.</p>
            <p>Absorption also depends on stomach acid, which decreases with age and with PPI use. Taking magnesium with food helps. Taking it away from calcium supplements helps more—the two compete for absorption.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">A practical approach</h2>
            <p>Assume you're not getting enough. The statistics support this: an estimated 50-60% of Americans fail to meet the RDA through diet alone, and the RDA may be set too low to begin with. <em>Supplementation isn't a luxury or optimization—for most people, it's filling a gap that modern life created.</em></p>
            <p>Start with 200mg of a well-absorbed form. Glycinate for evening, citrate for morning. Increase gradually; too much too fast causes loose stools. Spread doses throughout the day rather than taking everything at once. Give it a few weeks—magnesium repletion isn't instant.</p>

            <p className="italic text-gray-500 my-12 text-center">The supplement industry has many problems, but magnesium isn't snake oil. It's a mineral that we genuinely need more of, addressing a deficiency that we genuinely have. Sometimes the simple explanation is the correct one.</p>

            <div className="border-t border-gray-200 pt-8 mt-12">
              <p className="text-gray-600 mb-6">Ready to address the gap? <button onClick={() => navigateTo('home')} className="text-gray-900 underline hover:no-underline">Compare magnesium supplements</button> by form and absorption.</p>
              <div className="flex flex-wrap gap-2 mb-8">
                {postTags.map(tag => (
                  <button key={tag} onClick={() => handleTagClick(tag)} className="px-3 py-1 bg-white/60 hover:bg-white/80 backdrop-blur-sm border border-gray-200/50 text-gray-500 text-sm rounded-full transition-colors shadow-sm">
                    {tag}
                  </button>
                ))}
              </div>
              <ShareButtons title="Why You Can't Get Enough Magnesium From Food Anymore" />
            </div>
          </div>
        </article>
      </div>
    );
  }

  // Blog Post: Omega-3 Rancidity
  if (currentPage === 'blog-omega3') {
    const postTags = ['omega-3', 'fish oil', 'oxidation', 'quality control', 'inflammation'];
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
        <div className="bg-white/70 backdrop-blur-xl border-b border-gray-200/50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <button onClick={() => navigateTo('home')} className="flex items-center gap-2 hover:opacity-70 transition-opacity">
              <span className="text-xl text-gray-900 tracking-tight"><span className="font-semibold">vitamin</span><span className="font-light">bird</span></span>
            </button>
            <button onClick={() => navigateTo('blog')} className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1">
              <BookOpen size={16} />
              <span className="hidden sm:inline">All Articles</span>
            </button>
          </div>
        </div>
        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
          <header className="mb-8 sm:mb-12">
            <nav className="text-sm text-gray-400 mb-6 flex items-center gap-2">
              <button onClick={() => navigateTo('home')} className="hover:text-gray-600">Home</button>
              <ChevronRight size={14} />
              <button onClick={() => navigateTo('blog')} className="hover:text-gray-600">Blog</button>
              <ChevronRight size={14} />
              <span className="text-gray-600">Article</span>
            </nav>
            <h1 className="text-4xl font-serif mb-4 text-gray-900 leading-tight">Your Fish Oil Is Probably Rancid</h1>
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-6">
              <span>Rudy Scott</span>
              <span>·</span>
              <span>January 12, 2026</span>
            </div>
            <ShareButtons title="Your Fish Oil Is Probably Rancid" />
          </header>

          <figure className="my-10">
            <img src="https://images.unsplash.com/photo-1498654896293-37aacf113fd9?w=800&h=400&fit=crop" alt="Golden light through amber liquid" className="w-full h-64 object-cover rounded-lg" />
            <figcaption className="text-center text-gray-400 text-sm mt-3 italic">What's inside the capsule matters more than what's on the label.</figcaption>
          </figure>

          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-6">
            <p className="text-xl text-gray-600 leading-relaxed">Here's an uncomfortable truth about the omega-3 supplements sitting in your cabinet: studies consistently find that a significant percentage of fish oil products are oxidized beyond acceptable limits before you even open the bottle. And once oxidized, they may do more harm than good.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">The oxidation problem</h2>
            <p>Fish oil is inherently unstable. The same polyunsaturated fatty acids that make omega-3s beneficial—EPA and DHA—are highly susceptible to oxidation when exposed to heat, light, or oxygen. This process begins the moment fish are caught and continues through processing, encapsulation, shipping, and storage.</p>
            <p>A 2015 study published in Scientific Reports tested 171 over-the-counter fish oil supplements from New Zealand. <span className="bg-blue-100 px-1">More than 80% exceeded recommended oxidation limits.</span> Similar studies in the United States, Canada, and South Africa have found comparable results. The industry has a freshness problem it doesn't like to talk about.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">Why it matters</h2>
            <p>Oxidized fish oil doesn't just lose potency—it becomes a source of harmful compounds. <span className="bg-blue-100 px-1">Lipid peroxides, aldehydes, and other oxidation byproducts can increase inflammation</span>, the opposite of what you're trying to achieve. Some animal studies suggest oxidized omega-3s may promote atherosclerosis rather than prevent it.</p>
            <p>The fishy burps people complain about? <em>Often a sign of rancidity.</em> Fresh fish oil has minimal odor and taste. That overpowering fish smell means the oil has degraded.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">How to test your fish oil</h2>
            <p>Cut open a softgel and smell it. Taste it. Fresh fish oil should smell faintly oceanic at most—like a clean fish market, not a dumpster behind one. It shouldn't taste aggressively fishy or leave a lingering aftertaste. <span className="bg-blue-100 px-1">If you recoil, the oil is rancid.</span></p>
            <p>You can also check oxidation metrics if the manufacturer publishes them. Look for TOTOX value (total oxidation), peroxide value (PV), and anisidine value (AV). <em>TOTOX should be under 26, PV under 5, and AV under 20.</em> IFOS-certified products publish these numbers; many others don't.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">What causes rancidity</h2>
            <p>The supply chain is long. Fish are caught, transported to processing plants, rendered into oil, concentrated, encapsulated, shipped to distributors, warehoused, shipped again to retailers, and shelved—sometimes for months. Each step introduces oxidation risk.</p>
            <p>Ethyl ester forms oxidize faster than triglyceride forms. Clear bottles expose oil to light. Warehouses and shipping containers get hot. Products sit on shelves past their prime. <span className="bg-blue-100 px-1">By the time you swallow that softgel, the oil inside may have been degrading for over a year.</span></p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">Protective factors</h2>
            <p>Quality manufacturers add antioxidants—typically vitamin E (mixed tocopherols) or rosemary extract—to slow oxidation. They use nitrogen flushing during encapsulation to displace oxygen. They package in opaque or dark bottles. They maintain cold chains during storage and shipping. They test finished products and publish results.</p>
            <p><em>Budget brands skip these steps because they cost money.</em></p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">Choosing wisely</h2>
            <p>Buy from manufacturers that publish oxidation values. IFOS (International Fish Oil Standards) certification requires testing, and you can look up specific products on their website. Nordic Naturals, Carlson, and WHC publish their TOTOX values. Many popular brands do not.</p>
            <p>Choose triglyceride form over ethyl ester—it's more stable and absorbs better. <span className="bg-blue-100 px-1">Store your fish oil in the refrigerator or freezer once opened.</span> Check expiration dates and buy from retailers with high turnover. And always, always do the smell test.</p>

            <p className="italic text-gray-500 my-12 text-center">The irony of fish oil supplementation is that in trying to reduce inflammation, many people are swallowing oxidized fats that may promote it. Freshness isn't a marketing gimmick—it's the difference between benefit and harm.</p>

            <div className="border-t border-gray-200 pt-8 mt-12">
              <p className="text-gray-600 mb-6">Looking for fresh, tested options? <button onClick={() => navigateTo('home')} className="text-gray-900 underline hover:no-underline">Compare omega-3 supplements</button> with published oxidation values.</p>
              <div className="flex flex-wrap gap-2 mb-8">
                {postTags.map(tag => (
                  <button key={tag} onClick={() => handleTagClick(tag)} className="px-3 py-1 bg-white/60 hover:bg-white/80 backdrop-blur-sm border border-gray-200/50 text-gray-500 text-sm rounded-full transition-colors shadow-sm">
                    {tag}
                  </button>
                ))}
              </div>
              <ShareButtons title="Your Fish Oil Is Probably Rancid" />
            </div>
          </div>
        </article>
      </div>
    );
  }

  // Blog Post: Vitamin D and K2
  if (currentPage === 'blog-vitamin-d') {
    const postTags = ['vitamin d', 'vitamin k2', 'calcium', 'bone health', 'cardiovascular'];
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
        <div className="bg-white/70 backdrop-blur-xl border-b border-gray-200/50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <button onClick={() => navigateTo('home')} className="flex items-center gap-2 hover:opacity-70 transition-opacity">
              <span className="text-xl text-gray-900 tracking-tight"><span className="font-semibold">vitamin</span><span className="font-light">bird</span></span>
            </button>
            <button onClick={() => navigateTo('blog')} className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1">
              <BookOpen size={16} />
              <span className="hidden sm:inline">All Articles</span>
            </button>
          </div>
        </div>
        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
          <header className="mb-8 sm:mb-12">
            <nav className="text-sm text-gray-400 mb-6 flex items-center gap-2">
              <button onClick={() => navigateTo('home')} className="hover:text-gray-600">Home</button>
              <ChevronRight size={14} />
              <button onClick={() => navigateTo('blog')} className="hover:text-gray-600">Blog</button>
              <ChevronRight size={14} />
              <span className="text-gray-600">Article</span>
            </nav>
            <h1 className="text-4xl font-serif mb-4 text-gray-900 leading-tight">The Vitamin D Mistake Almost Everyone Makes</h1>
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-6">
              <span>Rudy Scott</span>
              <span>·</span>
              <span>January 10, 2026</span>
            </div>
            <ShareButtons title="The Vitamin D Mistake Almost Everyone Makes" />
          </header>

          <figure className="my-10">
            <img src="https://images.unsplash.com/photo-1530268729831-4b0b9e170218?w=800&h=400&fit=crop" alt="Morning sunlight streaming through window blinds" className="w-full h-64 object-cover rounded-lg" />
            <figcaption className="text-center text-gray-400 text-sm mt-3 italic">The sunshine vitamin needs partners to do its job properly.</figcaption>
          </figure>

          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-6">
            <p className="text-xl text-gray-600 leading-relaxed">Vitamin D supplementation has become almost universal. Doctors recommend it, health influencers promote it, and studies link deficiency to everything from depression to cancer. But there's a critical piece missing from this conversation: what happens to all that extra calcium your body absorbs when you take high-dose vitamin D without its essential partner.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">The calcium paradox</h2>
            <p>Vitamin D's primary job is to increase calcium absorption from your gut. Take enough D, and your body becomes remarkably efficient at pulling calcium from food. This sounds good until you realize that <span className="bg-blue-100 px-1">calcium doesn't automatically know where to go once it enters your bloodstream.</span></p>
            <p>Calcium can deposit in your bones, which is the goal. But it can also accumulate in soft tissues—your arteries, kidneys, and heart valves. This process, called ectopic calcification, is associated with cardiovascular disease, kidney stones, and other problems you'd rather avoid.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">Enter vitamin K2</h2>
            <p>Vitamin K2 activates proteins that direct calcium traffic. <span className="bg-blue-100 px-1">Matrix GLA protein (MGP), when activated by K2, prevents calcium from depositing in arterial walls.</span> Osteocalcin, also K2-dependent, helps bind calcium to bone matrix. Without adequate K2, these proteins remain inactive, and calcium goes wherever it pleases.</p>
            <p><em>Think of vitamin D as the bouncer letting calcium into the club, and K2 as the host directing guests to their tables. Without the host, people mill around aimlessly and cause problems.</em></p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">The evidence</h2>
            <p>The Rotterdam Study followed nearly 5,000 people and found that <span className="bg-blue-100 px-1">high vitamin K2 intake was associated with a 52% lower risk of severe arterial calcification and a 57% lower risk of dying from heart disease.</span> Other research has shown that K2 deficiency is common in people with arterial calcification, even when their vitamin D levels are adequate.</p>
            <p>A concerning pattern has emerged in calcium supplementation trials. Some studies have found that calcium supplements—which, like vitamin D, increase circulating calcium—may increase heart attack risk. The suspected mechanism: calcium flooding the bloodstream without the K2 needed to direct it properly.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">The modern deficiency</h2>
            <p>Vitamin K2 is found primarily in fermented foods (natto, aged cheeses, sauerkraut) and animal products from grass-fed animals. <span className="bg-blue-100 px-1">The Western diet, heavy on grain-fed meat and light on fermentation, provides little K2.</span> Meanwhile, we're taking more vitamin D than ever.</p>
            <p>This mismatch—abundant D, scarce K2—may explain why some high-dose vitamin D trials have shown disappointing cardiovascular results. <em>We're increasing calcium absorption without ensuring it goes to the right places.</em></p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">What this means for supplementation</h2>
            <p>If you're taking more than 1000-2000 IU of vitamin D daily, consider adding K2. The MK-7 form, derived from natto fermentation, has the longest half-life and most research behind it. Doses of 100-200 mcg daily are commonly used, though optimal dosing isn't firmly established.</p>
            <p>Some researchers argue that anyone taking vitamin D should take K2, regardless of dose. The logic: <span className="bg-blue-100 px-1">why risk suboptimal calcium distribution when the solution is simple and safe?</span></p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">The magnesium connection</h2>
            <p>There's another nutrient in this equation. Magnesium is required to convert vitamin D into its active form. Without enough magnesium, vitamin D supplementation may be less effective—and the unused D could potentially cause its own imbalances.</p>
            <p><span className="bg-blue-100 px-1">Roughly half of Americans don't get adequate magnesium from diet.</span> If you're supplementing D without addressing magnesium status, you're likely leaving benefits on the table.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">The complete picture</h2>
            <p>Vitamin D doesn't work in isolation. It's part of a system that includes K2 for calcium direction and magnesium for D activation. Supplementing one without considering the others may be ineffective at best and problematic at worst.</p>
            <p>The supplement industry has responded with D3+K2 combination products, which makes sense. But the messaging around vitamin D—"most people are deficient, just take more"—remains incomplete. <em>Deficiency matters, but so does the context in which you correct it.</em></p>

            <p className="italic text-gray-500 my-12 text-center">The goal isn't just to raise your vitamin D levels. It's to support the entire calcium metabolism system. That requires thinking beyond a single nutrient.</p>

            <div className="border-t border-gray-200 pt-8 mt-12">
              <p className="text-gray-600 mb-6">Looking for D3+K2 combinations? <button onClick={() => navigateTo('home')} className="text-gray-900 underline hover:no-underline">Compare supplements</button> that include both nutrients.</p>
              <div className="flex flex-wrap gap-2 mb-8">
                {postTags.map(tag => (
                  <button key={tag} onClick={() => handleTagClick(tag)} className="px-3 py-1 bg-white/60 hover:bg-white/80 backdrop-blur-sm border border-gray-200/50 text-gray-500 text-sm rounded-full transition-colors shadow-sm">
                    {tag}
                  </button>
                ))}
              </div>
              <ShareButtons title="The Vitamin D Mistake Almost Everyone Makes" />
            </div>
          </div>
        </article>
      </div>
    );
  }

  // Blog Post: In Defense of Synthetic Vitamins
  if (currentPage === 'blog-synthetic') {
    const postTags = ['synthetic vitamins', 'natural vs synthetic', 'bioavailability', 'food sources', 'dosing'];
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
        <div className="bg-white/70 backdrop-blur-xl border-b border-gray-200/50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <button onClick={() => navigateTo('home')} className="flex items-center gap-2 hover:opacity-70 transition-opacity">
              <span className="text-xl text-gray-900 tracking-tight"><span className="font-semibold">vitamin</span><span className="font-light">bird</span></span>
            </button>
            <button onClick={() => navigateTo('blog')} className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1">
              <BookOpen size={16} />
              <span className="hidden sm:inline">All Articles</span>
            </button>
          </div>
        </div>
        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
          <header className="mb-8 sm:mb-12">
            <nav className="text-sm text-gray-400 mb-6 flex items-center gap-2">
              <button onClick={() => navigateTo('home')} className="hover:text-gray-600">Home</button>
              <ChevronRight size={14} />
              <button onClick={() => navigateTo('blog')} className="hover:text-gray-600">Blog</button>
              <ChevronRight size={14} />
              <span className="text-gray-600">Article</span>
            </nav>
            <h1 className="text-4xl font-serif mb-4 text-gray-900 leading-tight">In Defense of Synthetic Vitamins</h1>
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-6">
              <span>Rudy Scott</span>
              <span>·</span>
              <span>January 18, 2026</span>
            </div>
            <ShareButtons title="In Defense of Synthetic Vitamins" />
          </header>

          <figure className="my-10">
            <img src="https://images.unsplash.com/photo-1457530378978-8bac673b8062?w=800&h=400&fit=crop" alt="Rows of leafy greens stretching into the distance" className="w-full h-64 object-cover rounded-lg" />
            <figcaption className="text-center text-gray-400 text-sm mt-3 italic">You'd need to eat a lot more of this than you think.</figcaption>
          </figure>

          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-6">
            <p className="text-xl text-gray-600 leading-relaxed">The wellness industry has convinced us that "natural" vitamins from food are inherently superior to synthetic ones. The logic seems intuitive: our bodies evolved eating food, not pills. But when you actually do the math on what it would take to get therapeutic doses of certain nutrients from diet alone, the natural-is-always-better argument starts to fall apart.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">The vitamin D problem</h2>
            <p>Let's start with vitamin D, since deficiency is so common. The RDA is 600-800 IU, but many researchers argue that 2000-5000 IU daily is closer to optimal, especially for people with limited sun exposure.</p>
            <p>To get 2000 IU of vitamin D from food, you'd need to eat one of the following <em>every single day</em>:</p>
            <p className="pl-6 border-l-2 border-gray-200">About 20 eggs. Or 7 ounces of wild salmon. Or 50 ounces of fortified milk. Or roughly 15 cans of tuna.</p>
            <p><span className="bg-blue-100 px-1">Nobody eats 20 eggs a day.</span> The people who get adequate vitamin D from natural sources do so primarily through sun exposure—which isn't really "food" and comes with its own skin cancer tradeoffs. For the rest of us, supplementation isn't a shortcut around a healthy diet. It's the only practical option.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">The magnesium math</h2>
            <p>The RDA for magnesium is 310-420mg depending on age and sex. To hit 400mg from food, you'd need something like:</p>
            <p className="pl-6 border-l-2 border-gray-200">3 cups of cooked spinach. Or 2.5 cups of black beans. Or 14 ounces of almonds (about 2,400 calories worth). Or 8 avocados.</p>
            <p>These are the highest-magnesium foods that exist. Most people don't eat any of them in significant quantities on a regular basis. And remember—these calculations assume you're absorbing 100% of the magnesium in the food, which you're not. <span className="bg-blue-100 px-1">Actual absorption rates vary from 20-50% depending on the food and your gut health.</span></p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">Omega-3s require a fish habit</h2>
            <p>The American Heart Association recommends 1000mg of EPA+DHA daily for people with heart disease, and many researchers suggest this amount benefits everyone. To get there through food:</p>
            <p className="pl-6 border-l-2 border-gray-200">About 3 ounces of wild salmon daily. Or 6 ounces of sardines. Or 12 ounces of farmed salmon. Or about a pound of shrimp.</p>
            <p>People in coastal Japan and traditional Inuit populations historically achieved these intakes. <em>You probably don't.</em> Unless you're eating fatty fish four or more times per week, you're not getting meaningful omega-3s from diet—plant sources like flax and chia contain ALA, which converts to EPA and DHA at roughly 5-10% efficiency.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">B12 requires animal products</h2>
            <p>Vitamin B12 is found almost exclusively in animal products. The RDA is only 2.4mcg, which sounds easy to hit. And for omnivores, it usually is—a single serving of meat or eggs covers it.</p>
            <p>But here's the catch: <span className="bg-blue-100 px-1">absorption of B12 from food decreases significantly with age</span>, dropping to 10-30% in many older adults due to reduced stomach acid. For someone over 50 with absorption issues, getting adequate B12 from food alone becomes genuinely difficult. The synthetic form in supplements, ironically, is often better absorbed because it's not bound to food proteins.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">When synthetic is chemically identical</h2>
            <p>Here's what the "natural is better" crowd often misses: many synthetic vitamins are molecularly identical to their natural counterparts. Vitamin C is vitamin C, whether it comes from an orange or a lab. Your body can't tell the difference because there is no difference.</p>
            <p>Ascorbic acid synthesized in a factory has the exact same chemical structure as ascorbic acid in a bell pepper. The atoms are arranged identically. The biological activity is identical. <em>The only difference is the story we tell ourselves about where it came from.</em></p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">Where form actually matters</h2>
            <p>This isn't to say all synthetic vitamins are equivalent to food forms. There are legitimate differences worth knowing about:</p>
            <p><span className="bg-blue-100 px-1">Folate vs folic acid:</span> Folic acid is synthetic and requires conversion to be useful. Some people convert it poorly. Methylfolate (5-MTHF) is the active form and works for everyone.</p>
            <p><span className="bg-blue-100 px-1">Vitamin E:</span> Synthetic vitamin E (dl-alpha-tocopherol) is a mix of eight stereoisomers, only one of which matches the natural form. Natural vitamin E (d-alpha-tocopherol) is more bioavailable. This is one case where "natural" genuinely matters.</p>
            <p><span className="bg-blue-100 px-1">B12:</span> Cyanocobalamin is synthetic and contains a cyanide molecule. Methylcobalamin and adenosylcobalamin are the forms your body actually uses. The cyanide amounts are tiny and harmless for most people, but why bother when better options exist?</p>
            <p>The point isn't that all synthetics are fine. It's that the synthetic/natural distinction isn't the right way to evaluate a supplement. <em>Form matters. Source often doesn't.</em></p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">Food has things pills don't</h2>
            <p>None of this means you should replace food with supplements. Whole foods contain fiber, phytonutrients, and thousands of compounds we haven't fully characterized. They provide the matrix in which nutrients naturally occur together—vitamin C with bioflavonoids, vitamin E with selenium, and so on.</p>
            <p>Eating well matters enormously for health. But "eat a good diet" and "take targeted supplements" aren't mutually exclusive strategies. For many nutrients, <span className="bg-blue-100 px-1">the dose you'd need for therapeutic benefit simply cannot be obtained through food without absurd quantities.</span></p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">The real question</h2>
            <p>Instead of asking "is this natural or synthetic?", ask better questions: Is this the bioavailable form? Is the dose meaningful? Is it third-party tested? Does the research support this use?</p>
            <p>A well-formulated synthetic supplement will outperform a poorly-formulated "whole food" supplement every time. And for nutrients where food simply can't deliver therapeutic doses—which is many of them—synthetic isn't a compromise. It's the only option that works.</p>

            <p className="italic text-gray-500 my-12 text-center">The natural-versus-synthetic debate is a distraction. The real questions are about form, dose, and quality. Get those right, and the origin of the molecules matters far less than the wellness industry wants you to believe.</p>

            <div className="border-t border-gray-200 pt-8 mt-12">
              <p className="text-gray-600 mb-6">Ready to find well-formulated supplements? <button onClick={() => navigateTo('home')} className="text-gray-900 underline hover:no-underline">Compare options</button> based on bioavailability and quality.</p>
              <div className="flex flex-wrap gap-2 mb-8">
                {postTags.map(tag => (
                  <button key={tag} onClick={() => handleTagClick(tag)} className="px-3 py-1 bg-white/60 hover:bg-white/80 backdrop-blur-sm border border-gray-200/50 text-gray-500 text-sm rounded-full transition-colors shadow-sm">
                    {tag}
                  </button>
                ))}
              </div>
              <ShareButtons title="In Defense of Synthetic Vitamins" />
            </div>
          </div>
        </article>
      </div>
    );
  }

  // Blog Post: B Vitamins
  if (currentPage === 'blog-quality-guide') {
    const postTags = ['b vitamins', 'toxicity', 'synthetic vitamins', 'methylation', 'manufacturing'];
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
        <div className="bg-white/70 backdrop-blur-xl border-b border-gray-200/50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <button onClick={() => navigateTo('home')} className="flex items-center gap-2 hover:opacity-70 transition-opacity">
              <span className="text-xl text-gray-900 tracking-tight"><span className="font-semibold">vitamin</span><span className="font-light">bird</span></span>
            </button>
            <button onClick={() => navigateTo('blog')} className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1">
              <BookOpen size={16} />
              <span className="hidden sm:inline">All Articles</span>
            </button>
          </div>
        </div>
        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
          <header className="mb-8 sm:mb-12">
            <nav className="text-sm text-gray-400 mb-6 flex items-center gap-2">
              <button onClick={() => navigateTo('home')} className="hover:text-gray-600">Home</button>
              <ChevronRight size={14} />
              <button onClick={() => navigateTo('blog')} className="hover:text-gray-600">Blog</button>
              <ChevronRight size={14} />
              <span className="text-gray-600">Article</span>
            </nav>
            <h1 className="text-4xl font-serif mb-4 text-gray-900 leading-tight">The Dark Side of B Vitamins</h1>
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <span>Rudy Scott</span>
              <span>·</span>
              <span>January 8, 2026</span>
            </div>
          </header>

          <figure className="my-10">
            <img src="https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=800&h=400&fit=crop" alt="Industrial laboratory with steel equipment" className="w-full h-64 object-cover rounded-lg" />
            <figcaption className="text-center text-gray-400 text-sm mt-3 italic">Most vitamins are born in places that look nothing like a farm.</figcaption>
          </figure>

          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-6">
            <p className="text-xl text-gray-600 leading-relaxed">B vitamins are marketed as universally beneficial—energy boosters that anyone can take without concern. The reality is more complicated. Some synthetic forms are poorly metabolized. Others accumulate to toxic levels. And the way most B vitamins are manufactured would surprise you.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">How B vitamins are made</h2>
            <p>Most B vitamins in supplements don't come from food. They're synthesized industrially, often through bacterial fermentation or chemical processes. <span className="bg-blue-100 px-1">Vitamin B12 (cyanocobalamin) is produced by genetically modified bacteria in industrial fermenters.</span> Riboflavin (B2) is typically made by fermenting genetically modified Bacillus subtilis. Niacin (B3) is synthesized from 3-methylpyridine, a petrochemical derivative.</p>
            <p>This isn't necessarily bad—synthetic vitamins can be chemically identical to natural ones. But "chemically identical" doesn't always mean "metabolically identical." <em>How your body processes a vitamin depends on its specific molecular form</em>, and the cheapest forms aren't always the best absorbed.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">The folic acid problem</h2>
            <p>Folic acid is the synthetic form of folate, and it's everywhere—in supplements, fortified foods, and prenatal vitamins. Your body can't use folic acid directly. It must be converted to methylfolate (5-MTHF) through a multi-step process that requires a functioning MTHFR enzyme.</p>
            <p>Here's the problem: <span className="bg-blue-100 px-1">roughly 40% of the population has genetic variants that reduce MTHFR efficiency.</span> For these people, folic acid conversion is slow and incomplete. Unmetabolized folic acid accumulates in the bloodstream. High levels have been associated with reduced natural killer cell activity, potentially affecting immune function and cancer surveillance.</p>
            <p>The solution exists—methylfolate supplements bypass the conversion issue entirely—but most cheap multivitamins still use folic acid because it's cheaper and more stable.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">Cyanocobalamin's cyanide problem</h2>
            <p>The most common supplemental form of B12 is cyanocobalamin. <span className="bg-blue-100 px-1">As the name suggests, it contains a cyanide molecule.</span> Your body must cleave off this cyanide group before it can use the B12 portion. The cyanide is then detoxified—a process that requires resources and works less efficiently in some individuals.</p>
            <p>The amounts involved are tiny, and healthy people handle them fine. But why introduce unnecessary cyanide when better options exist? Methylcobalamin and adenosylcobalamin are the forms your body actually uses. They cost more to manufacture, but they're immediately bioavailable.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">When B6 becomes toxic</h2>
            <p>Pyridoxine (vitamin B6) is water-soluble, so conventional wisdom holds that excess simply washes out. <span className="bg-blue-100 px-1">This is dangerously wrong.</span> High-dose B6 supplementation—often found in energy drinks, B-complexes, and supplements marketed for PMS or carpal tunnel—can cause peripheral neuropathy.</p>
            <p>The mechanism is particularly insidious. Pyridoxine at high doses actually competes with pyridoxal-5-phosphate (P5P), the active form of B6, at receptor sites. Too much of the inactive form blocks the active form. Symptoms include numbness, tingling, and burning sensations in the extremities. Some cases have progressed to difficulty walking. <em>Recovery after discontinuation can take months or years; some damage may be permanent.</em></p>
            <p>The threshold varies by individual, but <span className="bg-blue-100 px-1">problems have been reported at doses as low as 100mg daily with chronic use.</span> Many B-complex supplements contain 50-100mg per serving. People sometimes take multiple products containing B6 without realizing the cumulative dose.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">Niacin's flush and its limits</h2>
            <p>High-dose niacin (B3) causes the infamous "niacin flush"—a dramatic skin flushing accompanied by warmth, redness, and sometimes itching. This is a prostaglandin-mediated response, not an allergic reaction, and it fades with continued use. Many people actually seek out this effect, believing it indicates the vitamin is "working."</p>
            <p>Niacin has legitimate medical uses for cholesterol management at doses of 1-3 grams daily. But at these levels, it requires medical supervision. <span className="bg-blue-100 px-1">Side effects can include liver damage, elevated blood sugar, and gout flares.</span> The "no-flush" niacinamide form avoids the flushing but also lacks the cholesterol benefits—and still carries risks at high doses, including potential liver toxicity above 3 grams daily.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">The energy drink deception</h2>
            <p>Energy drinks and "vitamin shots" often contain astronomical doses of B vitamins—5000% of the daily value for B12 is common. The marketing implies that more B vitamins means more energy. This is misleading at best.</p>
            <p>B vitamins are cofactors in energy metabolism, but they don't directly provide energy. If you're deficient, correcting that deficiency will help. If you're not deficient, megadoses won't give you extra energy—they'll just create expensive urine and potentially cause problems. <em>The energy you feel from these products comes from the caffeine, not the B vitamins.</em></p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">What "bioavailable forms" actually means</h2>
            <p>The supplement industry has begun marketing "active" or "methylated" B vitamins as premium options. Here's what that means in practice:</p>
            <p><em>Folate:</em> Choose methylfolate (5-MTHF) or folinic acid over folic acid. Critical if you have MTHFR variants, beneficial regardless.</p>
            <p><em>B12:</em> Choose methylcobalamin or adenosylcobalamin over cyanocobalamin. The body uses these forms directly.</p>
            <p><em>B6:</em> Choose pyridoxal-5-phosphate (P5P) over pyridoxine. Lower doses of P5P achieve the same effect as higher doses of pyridoxine, with less toxicity risk.</p>
            <p><em>Riboflavin:</em> Riboflavin-5-phosphate is the active form, though standard riboflavin converts reasonably well in most people.</p>

            <h2 className="text-2xl font-serif mt-12 mb-6 text-gray-900">A more thoughtful approach</h2>
            <p>B vitamins aren't inherently dangerous, but they're not candy either. <span className="bg-blue-100 px-1">The "water-soluble means safe" mantra has led to reckless dosing.</span> A good B-complex should contain reasonable amounts of bioavailable forms—not megadoses of cheap synthetic versions.</p>
            <p>Check what you're taking. Add up B6 from all sources. Look at the forms, not just the amounts. Question whether you need a B-complex at all, or whether targeted supplementation based on actual deficiency makes more sense.</p>

            <p className="italic text-gray-500 my-12 text-center">The supplement industry profits from the perception that more is better and that water-soluble vitamins are risk-free. Neither is true. Informed supplementation requires understanding not just what you're taking, but what form it's in and what your body does with it.</p>

            <div className="border-t border-gray-200 pt-8 mt-12">
              <p className="text-gray-600 mb-6">Looking for quality B vitamins? <button onClick={() => navigateTo('home')} className="text-gray-900 underline hover:no-underline">Compare supplements</button> with bioavailable forms and appropriate dosing.</p>
              <div className="flex flex-wrap gap-2">
                {postTags.map(tag => (
                  <button key={tag} onClick={() => handleTagClick(tag)} className="px-3 py-1 bg-white/60 hover:bg-white/80 backdrop-blur-sm border border-gray-200/50 text-gray-500 text-sm rounded-full transition-colors shadow-sm">
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </article>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 ${textColor} transition-colors duration-300`}>
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 sticky top-0 z-10 shadow-lg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex justify-between items-center gap-3 sm:gap-6">
            <button
              onClick={() => {
                setStep(1);
                setError('');
                setResults([]);
                setResearchSummary('');
                setPersonalizedDosage('');
                setInteractionWarning('');
              }}
              className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity cursor-pointer flex-shrink-0"
            >
              <span className="text-xl sm:text-3xl text-white whitespace-nowrap tracking-tight"><span className="font-semibold">vitamin</span><span className="font-light text-white/70">bird</span></span>
            </button>

            {/* Mobile menu icon */}
            <div className="flex items-center gap-2 md:hidden flex-1 justify-end">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg hover:bg-white/20 text-white transition-colors"
              >
                <Menu size={22} />
              </button>
            </div>

            {/* Desktop nav buttons */}
            <div className="hidden md:flex items-center gap-1">
              <button
                onClick={() => navigateTo('about')}
                className="px-4 py-2 rounded-xl transition-colors text-sm font-medium hover:bg-white/20 text-white"
              >
                About
              </button>
              <button
                onClick={() => navigateTo('contact')}
                className="px-4 py-2 rounded-xl transition-colors text-sm font-medium hover:bg-white/20 text-white"
              >
                Contact
              </button>
              <button
                onClick={() => navigateTo('blog')}
                className="px-4 py-2 rounded-xl transition-colors items-center gap-2 text-sm font-medium hover:bg-white/20 text-white flex"
              >
                <BookOpen size={18} />
                <span>Blog</span>
              </button>
            </div>
          </div>

          {/* Mobile menu dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-3 border-t border-white/20 pt-4 space-y-1">
              <button
                onClick={() => { navigateTo('about'); setMobileMenuOpen(false); }}
                className="w-full py-2.5 text-left text-white hover:bg-white/10 rounded-lg flex items-center gap-2 px-2 transition-colors"
              >
                About
              </button>
              <button
                onClick={() => { navigateTo('contact'); setMobileMenuOpen(false); }}
                className="w-full py-2.5 text-left text-white hover:bg-white/10 rounded-lg flex items-center gap-2 px-2 transition-colors"
              >
                Contact
              </button>
              <button
                onClick={() => { navigateTo('blog'); setMobileMenuOpen(false); }}
                className="w-full py-2.5 text-left text-white hover:bg-white/10 rounded-lg flex items-center gap-2 px-2 transition-colors"
              >
                <BookOpen size={18} />
                Blog
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {step === 1 && (
          <div className="space-y-8">
            {/* Form Section - Wizard Style */}
            <div className="bg-white/60 backdrop-blur-xl border border-gray-200/60 rounded-3xl p-5 sm:p-8 shadow-xl shadow-gray-200/50">
              <div className="text-center mb-10">
                <h1 className="text-2xl sm:text-4xl font-extrabold text-gray-800">
                  <span>Find the </span>
                  <span className="relative inline-block">
                    <span
                      key={headlineIndex}
                      className="text-blue-600 inline-block animate-slideUp"
                    >
                      {ROTATING_TEXT[headlineIndex]}
                    </span>
                  </span>
                </h1>
                <style>{`
                  @keyframes slideUp {
                    0% { opacity: 0; transform: translateY(20px); }
                    10% { opacity: 1; transform: translateY(0); }
                    90% { opacity: 1; transform: translateY(0); }
                    100% { opacity: 0; transform: translateY(-20px); }
                  }
                  .animate-slideUp {
                    animation: slideUp 3s ease-in-out;
                  }
                `}</style>
                <p className="text-gray-500 mt-3 text-base sm:text-lg">Based on the latest PubMed studies</p>
              </div>

              {/* Progress Steps */}
              <div className="flex items-center justify-center mb-8 sm:mb-10">
                <div className="flex items-center gap-2 sm:gap-4">
                  {[
                    { num: 1, label: 'About You', icon: User },
                    { num: 2, label: 'Supplement', icon: Pill },
                    { num: 3, label: 'Health', icon: Heart }
                  ].map((s, idx) => (
                    <div key={s.num} className="flex items-center">
                      <button
                        onClick={() => setWizardStep(s.num)}
                        className={`flex flex-col items-center ${wizardStep >= s.num ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                        disabled={wizardStep < s.num}
                      >
                        <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-all ${
                          wizardStep === s.num ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-200/50' :
                          wizardStep > s.num ? 'bg-blue-100 text-blue-600' :
                          'bg-gray-100 text-gray-400'
                        }`}>
                          {wizardStep > s.num ? <span className="text-xl">✓</span> : <s.icon size={26} />}
                        </div>
                        <span className={`text-sm mt-2 hidden sm:block font-medium ${wizardStep === s.num ? 'text-blue-600' : 'text-gray-400'}`}>
                          {s.label}
                        </span>
                      </button>
                      {idx < 2 && (
                        <div className={`w-12 sm:w-20 h-1 mx-2 sm:mx-3 rounded-full ${wizardStep > s.num ? 'bg-blue-300' : 'bg-gray-200'}`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Step 1: Personal Information */}
              {wizardStep === 1 && (
                <div className="bg-white/80 backdrop-blur-sm border border-gray-100 rounded-2xl p-4 sm:p-5 shadow-lg shadow-gray-100/50 max-w-xl mx-auto">
                  <h2 className="text-base font-bold text-gray-800 mb-1">Tell us about yourself</h2>
                  <p className="text-xs text-gray-500 mb-4">This helps us personalize dosage recommendations</p>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium mb-1.5">What's your age?</label>
                      <input
                        type="number"
                        value={formData.age}
                        onChange={(e) => setFormData({...formData, age: e.target.value})}
                        className="w-full px-3 py-2 bg-gray-50/80 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-300 focus:bg-white transition-all text-sm"
                        placeholder="Enter your age"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1.5">What's your gender?</label>
                      <div className="grid grid-cols-3 gap-2">
                        {['Male', 'Female', 'Other'].map((gender) => (
                          <button
                            key={gender}
                            type="button"
                            onClick={() => setFormData({...formData, gender})}
                            className={`py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                              formData.gender === gender
                                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white border-blue-400 shadow-md shadow-blue-200/50'
                                : 'bg-white/80 border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                            }`}
                          >
                            {gender}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 flex justify-end">
                    <button
                      onClick={() => setWizardStep(2)}
                      disabled={!formData.age || !formData.gender}
                      className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all shadow-md shadow-blue-200/50 text-sm"
                    >
                      Next <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Supplement Details */}
              {wizardStep === 2 && (
                <div className="bg-white/80 backdrop-blur-sm border border-gray-100 rounded-2xl p-4 sm:p-5 shadow-lg shadow-gray-100/50 max-w-xl mx-auto">
                  <h2 className="text-base font-bold text-gray-800 mb-1">What are you looking for?</h2>
                  <p className="text-xs text-gray-500 mb-4">Provide a supplement name, a health goal, or both</p>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium mb-1.5">Supplement name (optional)</label>
                      <input
                        type="text"
                        value={formData.supplement}
                        onChange={(e) => setFormData({...formData, supplement: e.target.value})}
                        className="w-full px-3 py-2 bg-gray-50/80 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-300 focus:bg-white transition-all text-sm"
                        placeholder="e.g., Magnesium, Vitamin D, Omega-3..."
                      />
                    </div>

                    <div className="relative py-1">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-200"></div>
                      </div>
                      <div className="relative flex justify-center">
                        <span className="bg-white px-3 text-xs text-gray-400">or</span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium mb-1.5">What's your health goal? (optional)</label>
                      <textarea
                        value={formData.goal}
                        onChange={(e) => setFormData({...formData, goal: e.target.value})}
                        rows={2}
                        className="w-full px-3 py-2 bg-gray-50/80 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-300 focus:bg-white transition-all text-sm"
                        placeholder="e.g., Better sleep, reduce anxiety, improve heart health..."
                      />
                    </div>

                    {/* Quick suggestions */}
                    <div>
                      <p className="text-xs text-gray-400 mb-1.5">Popular:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {['Omega-3', 'Magnesium', 'Vitamin D', 'B12', 'Probiotics'].map((supp) => (
                          <button
                            key={supp}
                            type="button"
                            onClick={() => setFormData({...formData, supplement: supp})}
                            className="px-2.5 py-1 text-xs bg-white/60 hover:bg-white/80 backdrop-blur-sm border border-gray-200/50 rounded-full text-gray-500 transition-colors shadow-sm"
                          >
                            {supp}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 flex justify-between">
                    <button
                      onClick={() => setWizardStep(1)}
                      className="px-5 py-2.5 bg-gray-100/80 text-gray-600 rounded-lg font-medium hover:bg-gray-200/80 backdrop-blur-sm flex items-center gap-2 transition-colors text-sm"
                    >
                      <ChevronLeft size={18} /> Back
                    </button>
                    <button
                      onClick={() => setWizardStep(3)}
                      disabled={!formData.supplement && !formData.goal}
                      className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all shadow-md shadow-blue-200/50 text-sm"
                    >
                      Next <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Health & Safety */}
              {wizardStep === 3 && (
                <div className="bg-white/80 backdrop-blur-sm border border-gray-100 rounded-2xl p-4 sm:p-5 shadow-lg shadow-gray-100/50 max-w-xl mx-auto">
                  <h2 className="text-base font-bold text-gray-800 mb-1">Health & Safety</h2>
                  <p className="text-xs text-gray-500 mb-4">Help us check for interactions and filter products</p>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium mb-2">Any allergies? (select all that apply)</label>
                      <div className="grid grid-cols-3 gap-1.5">
                        {['Shellfish', 'Soy', 'Dairy', 'Gluten', 'Tree Nuts', 'Fish'].map((allergy) => (
                          <button
                            key={allergy}
                            type="button"
                            onClick={() => {
                              const newAllergies = formData.allergies.includes(allergy)
                                ? formData.allergies.filter(a => a !== allergy)
                                : [...formData.allergies, allergy];
                              setFormData({...formData, allergies: newAllergies});
                            }}
                            className={`py-1.5 px-2 rounded-lg border text-xs transition-colors ${
                              formData.allergies.includes(allergy)
                                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white border-blue-400 shadow-sm'
                                : 'bg-white/80 border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                            }`}
                          >
                            {allergy}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium mb-1.5">Current medications (optional)</label>
                      <textarea
                        value={formData.medications}
                        onChange={(e) => setFormData({...formData, medications: e.target.value})}
                        rows={2}
                        className="w-full px-3 py-2 bg-gray-50/80 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-300 focus:bg-white transition-all text-sm"
                        placeholder="List any medications you're currently taking..."
                      />
                      <p className="text-xs mt-1 text-gray-400">We'll check for potential interactions</p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium mb-2">Dietary preferences (optional)</label>
                      <div className="grid grid-cols-2 gap-2">
                        {['Vegan', 'Kosher'].map((restriction) => (
                          <button
                            key={restriction}
                            type="button"
                            onClick={() => {
                              const newRestrictions = formData.dietaryRestrictions.includes(restriction)
                                ? formData.dietaryRestrictions.filter(r => r !== restriction)
                                : [...formData.dietaryRestrictions, restriction];
                              setFormData({...formData, dietaryRestrictions: newRestrictions});
                            }}
                            className={`py-1.5 rounded-lg border text-sm transition-colors ${
                              formData.dietaryRestrictions.includes(restriction)
                                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white border-blue-400 shadow-sm'
                                : 'bg-white/80 border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                            }`}
                          >
                            {restriction}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 flex justify-between gap-3">
                    <button
                      onClick={() => setWizardStep(2)}
                      className="px-5 py-2.5 bg-gray-100/80 text-gray-600 rounded-lg font-medium hover:bg-gray-200/80 backdrop-blur-sm flex items-center justify-center gap-2 transition-colors text-sm"
                    >
                      <ChevronLeft size={18} /> Back
                    </button>
                    <button
                      onClick={handleNext}
                      className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-indigo-600 flex items-center justify-center gap-2 transition-all shadow-md shadow-blue-300/40 text-sm"
                    >
                      Find My Supplement <ChevronRight size={22} />
                    </button>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50/70 backdrop-blur-lg border border-red-200/60 rounded-2xl p-4 mt-4 shadow-sm">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="text-red-500 flex-shrink-0" size={20} />
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                </div>
              )}
            </div>

          </div>
        )}

        {step === 2 && (
          <div className="space-y-8 text-center py-12">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <div>
              <h2 className="text-2xl font-light mb-2">Finding optimal formulations</h2>
              <p className={mutedColor + " text-lg"}>
                {loadingStep === 0 && "Gathering information..."}
                {loadingStep === 1 && "Understanding your needs..."}
                {loadingStep === 2 && "Searching PubMed for peer-reviewed studies..."}
                {loadingStep === 3 && "Compiling a summary..."}
                {loadingStep === 4 && "Creating recommendation..."}
                {loadingStep === 5 && "Finding optimal supplements..."}
              </p>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-3xl font-light mb-2">Your personalized results</h2>
              <p className={mutedColor}>
                {formData.supplement && formData.goal
                  ? `Based on our research on the effects of ${formData.supplement} on ${formData.goal}`
                  : formData.supplement
                    ? `Based on our research on ${formData.supplement}`
                    : `Based on our research on supplements for ${formData.goal}`
                }
              </p>
              {/* Share/Bookmark Link */}
              {formData.supplement && (
                <button
                  onClick={() => {
                    const slug = formData.supplement.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                    const url = `${window.location.origin}/supplements/${slug}`;
                    navigator.clipboard.writeText(url);
                    alert('Link copied! Share this research page with others.');
                  }}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800"
                >
                  <Link2 size={14} />
                  <span>Copy shareable link</span>
                </button>
              )}
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="text-yellow-700 flex-shrink-0 mt-0.5" size={18} />
                <div>
                  <h3 className="text-sm font-bold mb-1 text-yellow-900">Medical Disclaimer</h3>
                  <p className="text-xs text-yellow-800 leading-relaxed">
                    This content is AI-generated and may contain errors. Always consult with a healthcare professional before starting any supplement.
                  </p>
                </div>
              </div>
            </div>

            {interactionWarning && (
              <div className={"border rounded p-4 " + (interactionWarning.includes('NO MAJOR INTERACTIONS') ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200')}>
                <div className="flex items-start gap-2">
                  <AlertCircle className={interactionWarning.includes('NO MAJOR INTERACTIONS') ? 'text-green-700' : 'text-red-700'} size={20} />
                  <div className="flex-1">
                    <h3 className={"text-lg font-bold mb-1 " + (interactionWarning.includes('NO MAJOR INTERACTIONS') ? 'text-green-900' : 'text-red-900')}>
                      Drug Interaction Check
                    </h3>
                    <div className={"text-base leading-relaxed " + (interactionWarning.includes('NO MAJOR INTERACTIONS') ? 'text-green-800' : 'text-red-800')}>
                      {renderCompactMarkdown(interactionWarning, true)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Top Row: Optimal Formulation & Research Summary side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Optimal Formulation & Dose with Buy Button */}
              {personalizedDosage && (
                <div className="bg-white/60 backdrop-blur-lg border border-gray-200/60 rounded-2xl p-5 shadow-sm flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-gray-900">Optimal Formulation & Dose</h3>
                    <button
                      onClick={() => setExpandedDosage(!expandedDosage)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {expandedDosage ? 'Show less' : 'Show more'}
                    </button>
                  </div>
                  <div className="text-gray-700 text-base leading-relaxed mb-4 flex-1">
                    {renderCompactMarkdown(personalizedDosage, expandedDosage)}
                  </div>
                  {formData.supplement && (
                    <a
                      href={`https://www.amazon.com/s?k=${encodeURIComponent(getSearchTerm(recommendedForm) || formData.supplement)}${AMAZON_TAG ? '&tag=' + AMAZON_TAG : ''}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg text-sm font-bold hover:from-green-600 hover:to-green-700 transition-all shadow-md self-start"
                    >
                      <span>🛍️</span>
                      <span>Buy {getSearchTerm(recommendedForm) || formData.supplement} on Amazon</span>
                      <span>→</span>
                    </a>
                  )}
                </div>
              )}

              {/* Research Summary */}
              {researchSummary && (
                <div className="bg-white/60 backdrop-blur-lg border border-gray-200/60 rounded-2xl p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-gray-900">Research Summary</h3>
                    <button
                      onClick={() => setExpandedResearch(!expandedResearch)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {expandedResearch ? 'Show less' : 'Show more'}
                    </button>
                  </div>
                  <div className="text-gray-700 text-base leading-relaxed">
                    {renderCompactMarkdown(researchSummary, expandedResearch)}
                  </div>
                </div>
              )}
            </div>

            {/* Stack Builder Section */}
            {stackRecommendations && stackRecommendations.length > 0 && (
              <div className="bg-gradient-to-br from-purple-50/80 to-blue-50/80 backdrop-blur-lg border border-purple-200/60 rounded-2xl p-5 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <span>🧬</span> Stack Builder
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">Supplements that pair well with {formData.supplement} for optimal results</p>
                  </div>
                  <button
                    onClick={() => setExpandedStack(!expandedStack)}
                    className="text-sm text-purple-600 hover:text-purple-800"
                  >
                    {expandedStack ? 'Show less' : 'Show more'}
                  </button>
                </div>

                {expandedStack && (
                  <div className="space-y-4">
                    {stackRecommendations.map((item, index) => {
                      const stackSlug = item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                      return (
                        <div key={index} className="bg-white/70 rounded-xl p-4 border border-gray-200/50">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <h4 className="font-bold text-gray-900 text-base">
                                <button
                                  onClick={() => navigateTo('supplement', stackSlug)}
                                  className="text-purple-700 hover:text-purple-900 hover:underline"
                                >
                                  {item.name}
                                </button>
                              </h4>
                              <p className="text-sm text-purple-700 mt-1 font-medium">{item.reason}</p>
                              <div className="mt-2 text-sm text-gray-700">
                                <span className="font-semibold">Optimal Form:</span> {item.optimalForm} • <span className="font-semibold">Dose:</span> {item.dose}
                              </div>
                              <p className="mt-2 text-sm text-gray-600 italic">
                                {item.keyFinding}
                              </p>
                              {item.citationLink ? (
                                <a
                                  href={item.citationLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 mt-2 text-xs text-blue-600 hover:text-blue-800 font-medium"
                                >
                                  <BookOpen size={12} />
                                  View PubMed Study →
                                </a>
                              ) : (
                                <span className="inline-flex items-center gap-1 mt-2 text-xs text-gray-400">
                                  <BookOpen size={12} />
                                  Citation pending...
                                </span>
                              )}
                            </div>
                            <a
                              href={`https://www.amazon.com/s?k=${encodeURIComponent(item.optimalForm || item.name)}${AMAZON_TAG ? '&tag=' + AMAZON_TAG : ''}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg text-xs font-bold hover:from-green-600 hover:to-green-700 transition-all shadow-sm"
                            >
                              <span>Buy</span>
                              <span>→</span>
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Affiliate Disclosure */}
            <div className="bg-blue-50/70 backdrop-blur-lg border border-blue-200/60 rounded-2xl p-4 shadow-sm">
              <p className="text-sm text-gray-800 text-center">
                <strong className="text-blue-700">Affiliate Disclosure:</strong> We participate in affiliate programs with partner retailers. When you purchase through our links, we earn a small commission at no extra cost to you.
              </p>
            </div>

            {/* Email Results Box */}
            <div className="mt-12 max-w-2xl mx-auto">
              <div className="bg-blue-50/70 backdrop-blur-lg border border-blue-200/60 rounded-2xl p-6 shadow-sm">
                <h3 className="text-lg font-bold mb-4 text-center text-gray-900">
                  Send These Results to Me by Email
                </h3>
                <p className="text-sm mb-4 text-center text-gray-600">
                  Get your personalized supplement recommendations, research summary, and product links delivered to your inbox
                </p>
                
                {!emailSent ? (
                  <form onSubmit={handleSendEmail} className="flex gap-2">
                    <input
                      type="email"
                      value={emailAddress}
                      onChange={(e) => setEmailAddress(e.target.value)}
                      placeholder="Enter your email address"
                      className="flex-1 px-4 py-3 bg-white/80 backdrop-blur-sm border border-gray-200/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-300 text-sm"
                      required
                    />
                    <button
                      type="submit"
                      className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all text-sm font-medium whitespace-nowrap shadow-md shadow-blue-200/50"
                    >
                      Send Results
                    </button>
                  </form>
                ) : (
                  <div className="bg-green-50/70 backdrop-blur-lg border border-green-200/60 rounded-2xl p-4 text-center shadow-sm">
                    <p className="text-green-800 font-medium">
                      ✓ Results sent successfully! Check your inbox.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-white/50 backdrop-blur-lg border-t border-gray-200/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          {/* Top row: Logo, Links, Copyright */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700 tracking-tight"><span className="font-semibold">vitamin</span><span className="font-light text-gray-400">bird</span></span>
            </div>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
              <button onClick={() => setCurrentPage('about')} className="text-gray-500 hover:text-gray-700 text-xs">About</button>
              <button onClick={() => setCurrentPage('privacy')} className="text-gray-500 hover:text-gray-700 text-xs">Privacy</button>
              <button onClick={() => setCurrentPage('terms')} className="text-gray-500 hover:text-gray-700 text-xs">Terms</button>
              <button onClick={() => setCurrentPage('contact')} className="text-gray-500 hover:text-gray-700 text-xs">Contact</button>
            </div>
            <p className="text-gray-400 text-xs">© {new Date().getFullYear()} Vitamin Bird</p>
          </div>

          {/* Disclaimers - horizontal layout */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-500 leading-relaxed">
            <div className="bg-white/40 rounded-lg p-3">
              <strong className="text-gray-600">Pricing:</strong> Prices may not reflect current retailer pricing. Verify prices on retailer sites before purchasing.
            </div>
            <div className="bg-white/40 rounded-lg p-3">
              <strong className="text-gray-600">Medical:</strong> AI-generated recommendations are for informational purposes only. Consult your doctor before starting any supplement.
            </div>
            <div className="bg-white/40 rounded-lg p-3">
              <strong className="text-gray-600">Affiliate:</strong> We earn commissions from partner site purchases at no extra cost to you.
            </div>
          </div>
        </div>
      </footer>
      <Analytics />
    </div>
  );
};

export default SupplementOptimizer;